<template>
    <AdminLayout>
        <div class="ph">
            <div class="ptitle">Verification <span>Queue Center</span></div>
        </div>
        <div class="view" id="view-ops">
            <LoadingSpinner :isLoading="isLoading" />
            <div class="page-wrap">
                <div class="ops-banner">⚠ Scoring weights and point values are not shown at this access level. Verify
                    each
                    signal accurately — completeness determines the Care Worker's tier.</div>


                <!-- VERIFICATION PANEL -->
                <div id="ops-verify-view">
                    <button @click="backToQueue()"
                        style="font-size:12px;color:var(--muted);background:none;border:none;cursor:pointer;margin-bottom:16px;display:flex;align-items:center;gap:6px;">←
                        Back to queue</button>
                    <div class="ops-profile" id="ops-profile-card">
                        <div class="ops-av">
                            <img v-if="cg.profile_photo" :src="cg.profile_photo" />
                            <div v-else>
                                {{ initials }}
                            </div>
                        </div>
                        <div>
                            <div class="ops-profile-name" id="ops-pname">{{ cg.fn }} {{ cg.ln }}</div>
                            <div class="ops-profile-meta" id="ops-pmeta">{{
                                cg.category_links
                                    .map(cat => cat.category_name)
                                    .join(' · ')
                            }} · {{ cg.state }}
                            </div>
                            <div class="ops-profile-ref" id="ops-pref">{{ cg.care_id }}</div>
                        </div>
                        <div style="margin-left:auto;" id="ops-tier-badge"></div>
                    </div>


                    <!-- A: IDENTITY SIGNALS -->
                    <div class="sig-block">
                        <div class="sig-block-title">A · Identity Signals <span class="api-badge auto">API
                                Automated</span>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">BVN Verified <span class="api-badge auto">Prembly API</span></div>
                                <div class="sig-sub">Submitted: <span id="bvn-submitted" class="sig-doc">{{ maskedBVN
                                        }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" v-model="cg.bvn_status"
                                    @change="saveVerificationReview('bvn')">
                                    <option value="api_pending">— API Pending —</option>
                                    <option value="api_verified">✓ API Verified</option>
                                    <option value="api_failed">✕ API Failed / Not found</option>
                                </select>
                            </div>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">NIN Verified <span class="api-badge auto">NIMC API</span></div>
                                <div class="sig-sub">Submitted: <span class="sig-doc" id="nin-submitted">{{ maskedNIN
                                }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" v-model="cg.nin_status"
                                    @change="saveVerificationReview('nin')">
                                    <option value="api_pending">— API Pending —</option>
                                    <option value="api_verified">✓ API Verified</option>
                                    <option value="api_failed">✕ API Failed / Not found</option>
                                </select>
                            </div>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Government ID <span class="api-badge manual">Manual + API</span>
                                </div>
                                <div class="sig-sub"><a class="sig-doc" :href="cg.documents?.id?.url"
                                        target="_blank">View uploaded document</a></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" v-model="cg.documents.id.status"
                                    @change="saveVerificationReview('government_id')">

                                    <option value="pending">— Pending —</option>
                                    <option value="api_verified">✓ API Verified — Clear</option>
                                    <option value="manual">✓ Manual — Sighted & Clear</option>
                                    <option value="rejected">✕ Rejected</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- B: SAFETY & ACCOUNTABILITY -->
                    <div class="sig-block">
                        <div class="sig-block-title">B · Safety & Accountability</div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Criminal Background Check <span class="api-badge manual">Manual —
                                        NPF</span></div>

                                <div class="sig-sub">Upload report from Nigeria Police Force</div>
                                <div v-if="cg.background.npf_record_file_url" class="sig-sub">
                                    <a :href="cg.background.npf_record_file_url" class="sig-doc" target="_blank">
                                        View Uploaded Document
                                    </a>
                                </div>
                            </div>
                            <div class="sig-right">

                                <select class="sig-select" id="v-criminal" v-model="cg.background.status"
                                    @change="saveVerificationReview('background_check')">

                                    <option value="pending">— Pending —</option>

                                    <option value="clear">
                                        ✓ Clear — No record found
                                    </option>

                                    <option value="flagged">
                                        ✕ CRIMINAL RECORD FOUND
                                    </option>
                                </select>

                                <div class="npf-upload" v-if="
                                    cg.background.status === 'clear' ||
                                    cg.background.status === 'flagged'
                                ">
                                    <div class="doc-upload-zone" @click="$refs.npfReport.click()" style="padding:12px;">
                                        <div class="doc-upload-label">
                                            Upload NPF Report
                                        </div>

                                        <div class="doc-upload-sub">
                                            Scanned or photographed report
                                        </div>

                                        <input ref="npfReport" type="file" accept="image/*,.pdf" style="display:none"
                                            @change="handleDocUpload($event, 'npf_report')" />
                                    </div>

                                    <!-- UPLOADED FILE CARD -->
                                    <div v-if="docs.npf_report.file" class="doc-uploaded-card">

                                        <!-- Preview -->
                                        <div class="doc-thumb">

                                            <!-- Image Preview -->
                                            <template v-if="docs.npf_report.preview">
                                                <img :src="docs.npf_report.preview" alt="Document Preview" />
                                            </template>

                                            <!-- PDF Preview -->
                                            <template v-else>
                                                <div class="pdf-thumb">
                                                    📄
                                                </div>
                                            </template>

                                        </div>

                                        <!-- File Info -->
                                        <div class="doc-meta">

                                            <div class="doc-label">
                                                Uploaded NPF Report
                                            </div>

                                            <div class="doc-name">
                                                {{ docs.npf_report.file.name }}
                                            </div>

                                        </div>

                                        <!-- Remove -->
                                        <button type="button" class="doc-remove-btn" @click="removeDoc('npf_report')">
                                            Remove
                                        </button>

                                    </div>

                                    <!-- SUCCESS -->
                                    <div v-if="docs.npf_report.file"
                                        style="margin-top:10px;color:green;font-size:13px;">
                                        ✅ File selected successfully
                                    </div>

                                    <!-- ERROR -->
                                    <div v-if="errors.npf_report" class="field-error">
                                        {{ errors.npf_report }}
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Guarantor Verified <span class="api-badge manual">Manual —
                                        Phone/In-Person</span></div>
                                <div class="sig-sub">Call guarantor to confirm: <span class="sig-doc" id="gname-show">{{
                                    cg.gname }}</span> · <span id="gphone-show"
                                        style="color:var(--dkgreen);font-size:10px;font-weight:600;">{{ cg.gphone
                                        }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" id="v-guarantor" v-model="cg.gstatus"
                                    @change="saveVerificationReview('guarantor')">
                                    <option value="pending">— Pending —</option>
                                    <option value="confirmed">✓ Confirmed — Guarantor verified</option>
                                    <option value="unverified">✕ Not reachable / Unverified</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- C: CREDENTIALS & EXPERIENCE -->
                    <div class="sig-block">
                        <div class="sig-block-title">C · Credentials & Experience</div>

                        <div class="sig-row">
                            <div class="sig-left">
                                <div class="sig-name">Education Level</div>
                                <div class="sig-sub">Submitted: <span id="edu-submitted"
                                        style="color:var(--green);font-weight:500;">{{
                                            selectedEducationLevels || 'Not provided'
                                        }}</span></div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" id="v-edu" v-model="cg.education"
                                    @change="saveVerificationReview('education_level')">

                                    <option v-for="edu in educationLevels" :key="edu.id" :value="edu.id">
                                        {{ edu.name }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="sig-row" v-if="cg.documents?.certificate">
                            <div class="sig-left">
                                <div class="sig-name">Certificate Verification</div>
                                <div class="sig-sub"><a class="sig-doc" :href="cg.documents?.certificate?.url"
                                        target="_blank">View uploaded
                                        certificate</a>
                                </div>
                            </div>
                            <div class="sig-right">
                                <select class="sig-select" id="v-cert" v-model="cg.documents.certificate.status"
                                    @change="saveVerificationReview('certificate_verification')">

                                    <option value="pending">— Pending —</option>
                                    <option value="verified_authentic">✓ Verified — Authentic</option>
                                    <option value="not_verified">⚠ Not Verified — Cannot confirm</option>
                                    <option value="forged">🚫 Forged / Fake — Certificate invalid</option>
                                </select>
                            </div>
                        </div>



                        <!-- Employment History — up to 5 entries -->
                        <div class="sig-row employment-wrapper">

                            <!-- Header -->
                            <div class="employment-header">
                                <div class="header-main-info">
                                    <div class="title-toggle-group" @click="toggleHistoryVisibility" role="button">
                                        <span class="toggle-icon" :class="{ rotated: showHistory }">▼</span>

                                        <div class="sig-name">Employment History (up to 5)</div>
                                    </div>

                                </div>


                                <div class="verified-counter">
                                    <span>Total Employers verified:</span>

                                    <!-- Auto count display -->
                                    <div class="verified-badge">
                                        {{ verifiedEmploymentCount }}/{{ cg.employment.length }}
                                    </div>
                                </div>
                            </div>

                            <!-- ───────────────────────────── -->
                            <!-- ADD EMPLOYMENT SECTION -->
                            <!-- ───────────────────────────── -->


                            <!-- ADD EMPLOYER FORM -->
                            <div v-if="showHistory" style="width:100%;">
                                <div id="emp-history-ops" style="width:100%;">

                                    <div class="emp-history-row" style="display:block;" v-if="cg.employment.length < 5">

                                        <div class="emp-row-header">

                                            <div class="emp-row-num">
                                                Add Employer
                                            </div>

                                        </div>

                                        <!-- ROW -->
                                        <div class="form-grid">

                                            <div class="field">
                                                <label>Employer Name</label>

                                                <input type="text" placeholder="Employer full name or company"
                                                    v-model="employmentForm.name">
                                            </div>

                                            <div class="field">
                                                <label>Employer Phone</label>

                                                <input type="tel" placeholder="0800 000 0000"
                                                    v-model="employmentForm.phone" @input="onPhoneInput">
                                            </div>

                                        </div>


                                        <!-- ROW -->
                                        <div class="form-grid">

                                            <div class="field">
                                                <label>Email</label>

                                                <input type="email" placeholder="Employer email"
                                                    v-model="employmentForm.email">
                                            </div>

                                            <div class="field">
                                                <label>Address</label>

                                                <input type="text" placeholder="Employer address"
                                                    v-model="employmentForm.address">
                                            </div>

                                        </div>

                                        <!-- BUTTON -->
                                        <button class="add-emp-btn" @click="saveEmployment"
                                            :disabled="isSavingEmployment">
                                            {{ isSavingEmployment ? 'Saving...' : '+ Add Employment History' }}
                                        </button>

                                    </div>

                                </div>

                                <div id="emp-history-ops" class="emp-history-wrapper" style="margin-top: 16px;">

                                    <div v-for="(emp, i) in cg.employment" :key="emp.id || i" class="emp-history-row">

                                        <!-- Top -->
                                        <div class="emp-row-header">

                                            <div class="emp-title">
                                                Employer #{{ i + 1 }}
                                            </div>

                                            <!-- Verification Toggle -->
                                            <label class="verify-toggle">

                                                <input type="checkbox" v-model="emp.verified"
                                                    @change="toggleEmploymentVerification(emp)">

                                                <span class="toggle-box"></span>

                                                <span class="verify-text" :class="{ verified: emp.verified }">
                                                    {{ emp.verified ? 'Verified' : 'Pending' }}
                                                </span>

                                            </label>

                                        </div>

                                        <!-- Info -->
                                        <div class="form-grid-emp">

                                            <div>
                                                <b>Name:</b> {{ emp.name }}
                                            </div>

                                            <div>
                                                <b>Phone:</b> {{ emp.phone }}
                                            </div>

                                        </div>



                                        <div class="form-grid-emp">

                                            <div>
                                                <b>Address:</b> {{ emp.address || '—' }}
                                            </div>

                                            <div>
                                                <b>Email:</b> {{ emp.email || '—' }}
                                            </div>

                                        </div>

                                    </div>

                                </div>
                                <div v-if="!cg.employment || cg.employment.length === 0" class="emp-history-empty">
                                    <span class="empty-icon">📁</span> No previous employer found.
                                </div>
                            </div>






                        </div>



                        <!-- ─────────────────────────────
 CARE TYPE VERIFICATION
───────────────────────────── -->

                        <div class="employment-wrapper" style="margin-top: 16px;">

                            <!-- Header -->
                            <div class="employment-header">
                                <div class="header-main-info">
                                    <div class="title-toggle-group" @click="toggleCareVisibility" role="button">
                                        <span class="toggle-icon" :class="{ crotated: showCareVerification }">▼</span>
                                        <div>
                                            <div class="sig-name">Care Type Coverage</div>
                                            <div class="sig-sub">Verify each care category claimed by the caregiver
                                            </div>
                                        </div>
                                    </div>


                                </div>

                                <!-- Verified Count -->
                                <div class="verify-counter">
                                    <div class="verify-counter-number">
                                        {{ verifiedCareTypes }}
                                    </div>
                                    <div class="verify-counter-label">
                                        Verified
                                    </div>
                                </div>
                            </div>

                            <div v-show="showCareVerification" class="care-verification-content">

                                <div v-if="!cg.category_links || !cg.category_links.length" class="care-empty">
                                    <span class="empty-icon">⚠️</span> No care categories submitted
                                </div>

                                <div v-else class="care-type-list">
                                    <div v-for="item in cg.category_links" :key="item.id" class="care-type-item"
                                        :class="{ verified: item.is_verified }">
                                        <div class="care-type-left">
                                            <label class="verify-check">
                                                <input type="checkbox" v-model="item.is_verified"
                                                    @change="toggleCareCategoryVerification(item)">
                                                <span class="checkmark"></span>
                                            </label>

                                            <div class="care-type-info">
                                                <div class="care-type-name">
                                                    {{ item.category_name }}
                                                </div>

                                                <div class="care-type-status">
                                                    <span v-if="item.is_verified" class="verified-badge">
                                                        Verified
                                                    </span>
                                                    <span v-else class="pending-badge">
                                                        Pending Verification
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="item.verified_at" class="verified-date">
                                            <span class="date-label">Verified:</span>
                                            <span class="date-value">{{ formatDate(item.verified_at) }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="care-summary">
                                    <div class="summary-item">
                                        <span class="summary-label">Total Submitted</span>
                                        <strong class="summary-value">{{ cg.category_links?.length || 0 }}</strong>
                                    </div>

                                    <div class="summary-item">
                                        <span class="summary-label">Total Verified</span>
                                        <strong class="summary-value green-text">
                                            {{ verifiedCareTypes }}
                                        </strong>
                                    </div>

                                    <div class="summary-item">
                                        <span class="summary-label">Pending</span>
                                        <strong class="summary-value orange-text">
                                            {{ (cg.category_links?.length || 0) - verifiedCareTypes }}
                                        </strong>
                                    </div>
                                </div>

                            </div>


                        </div>
                    </div>

                    <!-- D: REPUTATION -->
                    <div class="sig-block">
                        <div class="sig-block-title">D · Reputation</div>


                        <div class="section-title">D1 · Previous Employment</div>

                        <!-- ===================================== -->
                        <!-- EMPLOYER REVIEWS -->
                        <!-- ===================================== -->

                        <div class="employment-wrapper">

                            <!-- HEADER -->
                            <div class="employment-header">

                                <div class="header-main-info">
                                    <div class="title-toggle-group" @click="toggleReviewsVisibility" role="button">
                                        <span class="toggle-icon" :class="{ rotated: showReviews }">▼</span>
                                        <div class="sig-name">Previous Employer Reviews</div>
                                    </div>


                                </div>

                                <div style="
                display:flex;
                gap:10px;
                align-items:center;
            ">

                                    <div class="track-metrics-group">

                                        <div class="track-metric-card positive">
                                            <div class="metric-value">
                                                {{ totalPositiveReviews }}
                                            </div>
                                            <div class="metric-label">
                                                Positive
                                            </div>
                                        </div>

                                        <div class="track-metric-card negative">
                                            <div class="metric-value">
                                                {{ totalNegativeReviews }}
                                            </div>
                                            <div class="metric-label">
                                                Negative
                                            </div>
                                        </div>

                                        <div class="track-metric-card neutral">
                                            <div class="metric-value">
                                                {{ totalEmployerReviews }}
                                            </div>
                                            <div class="metric-label">
                                                Total Reviews
                                            </div>
                                        </div>


                                    </div>




                                </div>

                            </div>


                            <!-- ===================================== -->
                            <!-- REVIEW FORM -->
                            <!-- ===================================== -->

                            <div v-show="showReviews">
                                <div class="emp-history-row employment-review-form">

                                    <!-- ROW -->
                                    <div class="form-grid">

                                        <!-- SELECT EMPLOYER -->
                                        <div class="field">

                                            <label>
                                                Previous Employer
                                            </label>

                                            <select v-model="reviewForm.employment_id">

                                                <option disabled value="">
                                                    Select employer
                                                </option>

                                                <option v-for="emp in cg.employment" :key="emp.id" :value="emp.id">
                                                    {{ emp.name }}
                                                </option>

                                            </select>

                                        </div>

                                        <!-- RESULT -->
                                        <div class="field">

                                            <label>
                                                Review Result
                                            </label>

                                            <select v-model="reviewForm.result">

                                                <option value="positive">
                                                    Positive
                                                </option>

                                                <option value="negative">
                                                    Negative
                                                </option>

                                            </select>

                                        </div>

                                    </div>

                                    <!-- ROW -->
                                    <div class="form-grid-2">

                                        <div class="field">

                                            <label>
                                                Employer Review
                                            </label>

                                            <textarea placeholder="Write employer review..."
                                                v-model="reviewForm.review"></textarea>

                                        </div>

                                    </div>

                                    <!-- BUTTON -->
                                    <button class="add-emp-btn" @click="saveEmployerReview"
                                            :disabled="isSavingEmploymentReview">
                                            {{ isSavingEmploymentReview ? 'Saving...' : '+ Add Employer Review' }}
                                        </button>

                                </div>



                                <div class="reviews-section-container">



                                    <div class="reviews-section-content">

                                        <div v-if="allEmployerReviews.length" class="review-list">
                                            <div v-for="review in allEmployerReviews" :key="review.id"
                                                class="review-item">
                                                <div class="review-main-content">
                                                    <div class="review-header">
                                                        <h4 class="reviewer-name">{{ review.reviewer_name }}</h4>
                                                    </div>

                                                    <p class="review-body">
                                                        "{{ review.review }}"
                                                    </p>

                                                    <div class="review-footer">
                                                        <span class="reviewer-phone">{{ review.reviewer_phone }}</span>
                                                    </div>
                                                </div>

                                                <div class="review-meta">
                                                    <span class="review-status" :class="review.result.toLowerCase()">
                                                        {{ review.result }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-else class="emp-history-empty">
                                            <span class="empty-icon">💬</span> No employer reviews added yet.
                                        </div>

                                    </div>

                                </div>
                            </div>



                        </div>

                        <br />

                        <div class="section-title">D2 · Succour Deployment - MONTHLY</div>
                        <!-- ===================================================== -->
                        <!-- MONTHLY REPUTATION TRACKER -->
                        <!-- ===================================================== -->

                        <div class="employment-wrapper">

                            <!-- HEADER -->
                            <div class="employment-header">
                                <div class="header-main-info">

                                    <div class="title-toggle-group" @click="toggleSectionVisibility" role="button">

                                        <div class="toggle-icon" :class="{ rotated: showSection }">
                                            ▼
                                        </div>

                                        <div>
                                            <div class="sig-name">
                                                Monthly Reputation Timeline
                                            </div>

                                            <div class="sig-sub">
                                                Ongoing month-by-month caregiver performance monitoring
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <!-- METRICS -->
                                <div style="display: flex; gap: 10px; align-items: center;">

                                    <div class="track-metric-card positive">
                                        <div class="metric-value">
                                            {{ reputation.metrics?.total_monthly_positive_stats }}
                                        </div>
                                        <div class="metric-label">
                                            Positive
                                        </div>
                                    </div>

                                    <div class="track-metric-card negative">
                                        <div class="metric-value">
                                            {{ reputation.metrics?.total_monthly_negative_stats }}
                                        </div>
                                        <div class="metric-label">
                                            Negative
                                        </div>
                                    </div>

                                    <div class="track-metric-card neutral">
                                        <div class="metric-value">
                                            {{ reputation.metrics?.total_monthly_reviews_stats }}
                                        </div>
                                        <div class="metric-label">
                                            Total Months
                                        </div>
                                    </div>

                                </div>

                            </div>

                            <!-- BODY -->
                            <transition name="fade-slide">
                                <div v-show="showSection">

                                    <!-- ADD ENTRY -->
                                    <div v-if="!reputation.metrics?.deployment_max_cap_reached"
                                        class="emp-history-row employment-review-form">

                                        <div class="emp-row-header">
                                            <div>
                                                <div class="emp-row-num">
                                                    Add Monthly Review
                                                </div>

                                                <div class="sig-sub">
                                                    Log a caregiver monthly performance summary
                                                </div>
                                            </div>
                                        </div>

                                        <!-- FORM -->
                                        <div class="form-grid">

                                            <div class="field">
                                                <label>Reviewer Name</label>

                                                <input type="text" class="modern-input"
                                                    v-model="newEntry.mom_reviewer_name"
                                                    placeholder="Enter reviewer name">
                                            </div>

                                            <div class="field">
                                                <label>Phone Number</label>

                                                <input type="text" class="modern-input" v-model="newEntry.mom_phone"
                                                    placeholder="Enter phone number" @input="reviewerPhoneInput">
                                            </div>

                                        </div>

                                        <div class="form-grid">

                                            <div class="field">
                                                <label>Review Month</label>

                                                <input type="month" class="modern-input"
                                                    v-model="newEntry.review_month">
                                            </div>

                                            <div class="field">
                                                <label>Performance Result</label>

                                                <select class="modern-input" v-model="newEntry.result">
                                                    <option value="positive">
                                                        Positive Month
                                                    </option>

                                                    <option value="negative">
                                                        Negative Month
                                                    </option>
                                                </select>
                                            </div>

                                        </div>

                                        <div class="field">

                                            <label>Monthly Review Notes</label>

                                            <textarea class="modern-textarea" rows="4" v-model="newEntry.mom_reviews"
                                                placeholder="Enter detailed monthly performance observations..."></textarea>

                                        </div>

                                        <div class="monthly-action-row">

                                            <button class="add-emp-btn" :disabled="isSaving"
                                                @click="submitNewMonthlyReview">
                                                <span v-if="!isSaving">
                                                    + Save Monthly Entry
                                                </span>

                                                <span v-else>
                                                    Saving Review...
                                                </span>
                                            </button>

                                        </div>

                                    </div>

                                    <!-- LIMIT WARNING -->
                                    <div v-else class="timeline-limit-banner">
                                        <div class="limit-icon">
                                            ⚠️
                                        </div>

                                        <div>
                                            Maximum 60-month historical reputation timeline reached.
                                        </div>
                                    </div>

                                    <!-- TIMELINE -->
                                    <div v-if="reputation.reviews?.length" class="timeline-wrapper"
                                        style="margin-top: 16px;">

                                        <div v-for="item in reputation.reviews" :key="item.id" class="timeline-card"
                                            :class="item.result">

                                            <!-- LEFT LINE -->
                                            <div class="timeline-line">
                                                <div class="timeline-dot" :class="item.result"></div>
                                            </div>

                                            <!-- CONTENT -->
                                            <div class="timeline-content">

                                                <div class="timeline-top-row">

                                                    <div>
                                                        <div class="timeline-month">
                                                            {{ item.formatted_month }}
                                                        </div>

                                                        <div class="timeline-reviewer">
                                                            {{ item.mom_reviewer_name }}
                                                        </div>
                                                    </div>

                                                    <div class="timeline-status-badge" :class="item.result">
                                                        {{ item.result }}
                                                    </div>

                                                </div>

                                                <div class="timeline-phone">
                                                    📞 {{ item.mom_phone }}
                                                </div>

                                                <div class="timeline-review-text">
                                                    {{ item.mom_reviews }}
                                                </div>

                                            </div>

                                        </div>

                                        <!-- PAGINATION -->
                                        <div class="timeline-pagination" v-if="reputation.pagination?.total_pages > 1">

                                            <button class="pagination-btn"
                                                :disabled="!reputation.pagination?.has_previous"
                                                @click="fetchReviewsPage(reputation.pagination?.current_page - 1, cg.id)">
                                                ← Previous
                                            </button>

                                            <div class="pagination-indicator">
                                                Page
                                                {{ reputation.pagination?.current_page }}
                                                of
                                                {{ reputation.pagination?.total_pages }}
                                            </div>

                                            <button class="pagination-btn" :disabled="!reputation.pagination?.has_next"
                                                @click="fetchReviewsPage(reputation.pagination?.current_page + 1, cg.id)">
                                                Next →
                                            </button>

                                        </div>

                                    </div>

                                    <!-- EMPTY -->
                                    <div v-else class="monthly-empty-state">

                                        <div class="empty-icon">
                                            📁
                                        </div>

                                        <div class="empty-title">
                                            No Monthly Reviews Yet
                                        </div>

                                        <div class="empty-subtitle">
                                            Start building a month-by-month caregiver reputation history.
                                        </div>

                                    </div>

                                </div>
                            </transition>

                        </div>

                        <!-- MALPRACTICE SECTION -->
                        <div class="malpractice-section">

                            <!-- Header -->
                            <div class="malpractice-header">
                                <div class="header-main-info">
                                    <div class="title-toggle-group" @click="toggleMalpracticeVisibility" role="button">
                                        <span class="toggle-icon" :class="{ rotated: showMalpractice }">▼</span>
                                        <div>
                                            <div class="sig-name">Malpractice Escalations</div>
                                            <div class="sig-sub">Review and manage caregiver disciplinary records</div>

                                        </div>

                                    </div>
                                </div>

                                <!-- Stats -->
                                <div class="malpractice-stats">

                                    <div class="mal-stat danger">
                                        <span>{{ cg.malpractice.cases.length }}</span>
                                        <small>Total Cases</small>
                                    </div>

                                    <div class="mal-stat resolved">
                                        <span>
                                            {{
                                                cg.malpractice.cases.filter(
                                                    c => c.status === 'resolved'
                                                ).length
                                            }}
                                        </span>
                                        <small>Resolved Cases</small>
                                    </div>

                                    <div class="mal-stat pending">
                                        <span>
                                            {{
                                                cg.malpractice.cases.filter(
                                                    c => c.status !== 'resolved'
                                                ).length
                                            }}
                                        </span>
                                        <small>Pending Cases</small>
                                    </div>

                                </div>

                            </div>

                            <div style="width:100%;" v-show="showMalpractice">
                                <div class="emp-history-row">
                                    <div class="emp-row-header">
                                        Add Malpractice Case
                                    </div>




                                    <div class="form-grid" style="margin-top:16px;">

                                        <div class="field">
                                            <label>Title</label>

                                            <input v-model="newCase.title">
                                            <div v-if="errors.title" class="field-error">
                                                {{ errors.title }}
                                            </div>
                                        </div>
                                        <div class="field">
                                            <label>Severity</label>

                                            <select class="sig-select" v-model="newCase.severity">
                                                <option value="low">Low</option>
                                                <option value="medium">High</option>
                                                <option value="high">High</option>
                                                <option value="critical">Critical</option>
                                                <option value="severe">Severe</option>

                                            </select>
                                        </div>
                                    </div>

                                    <div class="form-grid" style="margin-top:16px;">

                                        <div class="field">
                                            <label>Status</label>

                                            <select v-model="newCase.status">
                                                <option value="open">Open</option>
                                                <option value="investigating">
                                                    Investigating
                                                </option>
                                                <option value="resolved">
                                                    Resolved
                                                </option>
                                                <option value="dismissed">
                                                    Dismissed
                                                </option>
                                            </select>
                                        </div>

                                        <div class="field">
                                            <label>Escalated By</label>

                                            <input v-model="newCase.escalated_by">
                                            <div v-if="errors.escalated_by" class="field-error">
                                                {{ errors.escalated_by }}
                                            </div>
                                        </div>

                                    </div>

                                    <div class="form-grid-2">
                                        <div class="field">
                                            <label>Description</label>

                                            <textarea rows="4" class="sig-textarea"
                                                v-model="newCase.description"></textarea>
                                            <div v-if="errors.description" class="field-error">
                                                {{ errors.description }}
                                            </div>
                                        </div>



                                    </div>

                                    <div class="form-grid-1" style="margin-top:16px;">


                                        <div class="field">
                                            <label>Case Document</label>

                                            <input type="file" @change="handleCaseDocument"
                                                accept=".pdf,.doc,.docx,image/*">
                                        </div>

                                        <div class="field">

                                            <label>Employment Restriction</label>

                                            <label class="action-toggle danger">

                                                <input type="checkbox" v-model="newCase.do_not_employ">

                                                <span class="action-box"></span>

                                                <span>
                                                    Do Not Employ
                                                </span>

                                            </label>

                                        </div>

                                    </div>


                                    <button class="add-emp-btn" @click="addMalpracticeCase" :disabled="isAdding">
                                        {{ isAdding ? 'Please waiting...' : 'Add Case' }}
                                    </button>


                                </div>



                                <div class="malpractice-section-container">

                                    <div class="malpractice-section-content">

                                        <div v-if="!cg.malpractice?.cases?.length" class="malpractice-empty">
                                            <span class="success-icon">✓</span> No malpractice record found
                                        </div>

                                        <div v-else class="malpractice-list">
                                            <div v-for="(item, i) in cg.malpractice.cases" :key="item.id || i"
                                                class="emp-history-row" style="margin-bottom: 12px;">
                                                <div class="malpractice-top">
                                                    <div class="emp-row-header">
                                                        <div class="sig-title">{{ item.title }}</div>
                                                        <div class="malpractice-meta">
                                                            Escalated by <span class="meta-author">{{ item.escalated_by
                                                                ||
                                                                'Unknown' }}</span>
                                                        </div>
                                                    </div>

                                                    <div class="severity-badge" :class="item.severity?.toLowerCase()">
                                                        {{ item.severity }}
                                                    </div>
                                                </div>

                                                <div class="form-grid-2" style="margin-top: 16px;">
                                                    <div class="field">
                                                        <label>Case Description</label>
                                                        <textarea class="form-control" rows="4"
                                                            v-model="item.description"></textarea>
                                                    </div>
                                                </div>

                                                <div class="form-grid" style="margin-top: 16px;">
                                                    <div class="field">
                                                        <label>Status</label>
                                                        <select class="form-control select-input" v-model="item.status">
                                                            <option value="open">Open</option>
                                                            <option value="investigating">Investigating</option>
                                                            <option value="resolved">Resolved</option>
                                                            <option value="dismissed">Dismissed</option>
                                                        </select>
                                                    </div>

                                                    <div class="field">
                                                        <label>Case Document</label>
                                                        <input type="file" class="file-input"
                                                            @change="replaceCaseDocument($event, item)"
                                                            accept=".pdf,.doc,.docx,image/*">
                                                        <div v-if="item.case_document_url" class="sig-sub">
                                                            <a :href="item.case_document_url" class="sig-doc"
                                                                target="_blank">
                                                                📄 View Uploaded Document
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div class="form-grid" style="margin-top: 16px;">
                                                    <div class="field">
                                                        <label>Created</label>
                                                        <span class="date-stamp">{{ formatDate(item.created_at)
                                                            }}</span>
                                                    </div>

                                                    <div class="field">
                                                        <label>Resolved</label>
                                                        <span class="date-stamp">
                                                            {{ item.resolved_at !== null ? formatDate(item.resolved_at)
                                                                :
                                                            '—' }}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div class="malpractice-actions">
                                                    <label class="action-toggle resolve">
                                                        <input type="checkbox" :checked="item.status === 'resolved'"
                                                            @change="toggleResolved(item)">
                                                        <span class="action-box"></span>
                                                        <span class="action-text">Mark Resolved</span>
                                                    </label>

                                                    <label class="action-toggle danger">
                                                        <input type="checkbox" v-model="item.do_not_employ">
                                                        <span class="action-box"></span>
                                                        <span class="action-text">Do Not Employ</span>
                                                    </label>
                                                </div>

                                                <div class="card-footer-action">
                                                    <button type="button" class="add-emp-btn" :disabled="isUpdating"
                                                        @click="updateMalpracticeCase(item)">
                                                        <span v-if="isUpdating" class="spinner-inline">⏳ Saving
                                                            Changes...</span>
                                                        <span v-else>Save Changes</span>
                                                    </button>
                                                </div>

                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>


                        </div>
                    </div>

                    <button class="compute-btn" @click="computeScore()">▶ Compute Trust Score & Assign Tier</button>

                    <!-- DNE Banner -->
                    <div class="dne-banner" id="dne-banner" v-if="score_result?.do_not_employ">
                        <div class="dne-title">🚫 DO NOT EMPLOY</div>

                        <div class="dne-sub">
                            <strong>Reasons:</strong>

                            <ul class="dne-reasons">
                                <li v-for="(reason, index) in dneReasons" :key="index">
                                    {{ reason }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Score Result -->
                    <div class="score-result" id="score-result" v-if="score_result">
                        <div class="result-label">TRUST SCORE RESULT</div>
                        <div class="sr-top">
                            <div>
                                <div class="sr-num" id="sr-num">{{ cg.verification?.trust_score }}</div>
                                <div class="sr-denom">/ 835</div>
                            </div>
                            <div class="sr-right">

                                <div class="sr-tier" :class="cg.verification.tier.toLowerCase()" id="sr-tier">
                                    {{ cg.verification.tier.toUpperCase() }} - {{
                                        cg.verification.deploy
                                            ? "Deployment-ready - Card ID can be issued"
                                            : "Not deployment-ready"
                                    }}
                                </div>
                                <div v-if="cg.verification.scores.silver_cap_reason" class="result-notes" id="r-notes">
                                    <div class="note dne">⛔ {{ cg.verification.scores.silver_cap_reason ||
                                        score_result.reason }}</div>
                                </div>
                                <div v-if="cg.verification.scores.bronze_cap_reason" class="result-notes" id="r-notes">
                                    <div class="note dne">⛔ {{ cg.verification.scores.bronze_cap_reason ||
                                        score_result.reason }}</div>
                                </div>
                                <div v-if="cg.verification.scores.gold_gate_failed" class="result-notes" id="r-notes">
                                    <div class="note dne">⛔ {{ cg.verification.scores.gold_gate_failed ||
                                        score_result.reason }}</div>
                                </div>

                                <!-- <div class="sr-deploy" id="sr-deploy">
                                    {{
                                        cg.verification.do_not_employ || !cg.verification.deploy
                                            ? "✕ Not deployment-ready"
                                            : "✓ Deployment-ready - Card ID can be issued"
                                    }}
                                </div> -->

                            </div>
                        </div>
                        <div class="sr-checklist" id="sr-checklist">

                            <!-- BVN -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.bvn > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.bvn > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">BVN Verified (API)</div>
                            </div>

                            <!-- NIN -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.nin > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.nin > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">NIN Verified (API)</div>
                            </div>

                            <!-- Government ID -->
                            <div class="sr-check">
                                <div class="sr-ck-icon"
                                    :class="cg.verification.scores.government_id > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.government_id > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Government ID Verified</div>
                            </div>

                            <!-- Criminal Check -->
                            <div class="sr-check">
                                <div class="sr-ck-icon"
                                    :class="cg.verification.scores.criminal_clearance > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.criminal_clearance > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Criminal check — clear</div>
                            </div>

                            <!-- Guarantor -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.guarantor > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.guarantor > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Guarantor verified</div>
                            </div>

                            <!-- Education -->
                            <div class="sr-check">
                                <div class="sr-ck-icon" :class="cg.verification.scores.education > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.education > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Education on record</div>
                            </div>

                            <!-- Employment -->
                            <div class="sr-check">
                                <div class="sr-ck-icon"
                                    :class="cg.verification.scores.employment_score > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.employment_score > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">Employment history verified</div>
                            </div>

                            <!-- Reviews -->
                            <div class="sr-check">
                                <div class="sr-ck-icon"
                                    :class="cg.verification.scores.reputation_score > 0 ? 'ok' : 'fail'">
                                    {{ cg.verification.scores.reputation_score > 0 ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">At least 1 good review</div>
                            </div>


                            <!-- Warning -->
                            <div class="sr-check warn" v-if="
                                cg.verification.scores.bronze_cap_reason
                            ">
                                <div class="sr-ck-icon warn">!</div>

                                <div class="sr-ck-lbl warn-text">
                                    {{ cg.verification.scores.bronze_cap_reason }}
                                </div>
                            </div>

                            <div class="sr-check warn" v-else-if="
                                cg.verification.scores.silver_cap_reason
                            ">
                                <div class="sr-ck-icon warn">!</div>

                                <div class="sr-ck-lbl warn-text">
                                    {{ cg.verification.scores.silver_cap_reason }}
                                </div>
                            </div>

                            <div class="sr-check warn" v-else-if="
                                cg.verification.scores.hard_stop_reason
                            ">
                                <div class="sr-ck-icon warn">!</div>

                                <div class="sr-ck-lbl warn-text">
                                    {{ cg.verification.scores.hard_stop_reason }}
                                </div>
                            </div>

                            <div class="sr-check danger" v-else-if="
                                cg.verification.scores.malpractice_penalty
                            ">
                                <div class="sr-ck-icon fail">✕</div>

                                <div class="sr-ck-lbl warn-text">
                                    Malpractice case(s) found
                                </div>
                            </div>

                            <!-- Gold Lock -->
                            <div class="sr-check" v-if="cg.verification.scores.gold_gate_failed">
                                <div class="sr-ck-icon" :class="cg.verification.tier === 'gold' ? 'ok' : 'fail'">
                                    {{ cg.verification.tier === 'gold' ? '✓' : '✕' }}
                                </div>

                                <div class="sr-ck-lbl">
                                    Gold not unlocked — requires identity + safety + credentials + >=12 positive succour
                                    deployment review
                                </div>
                            </div>

                        </div>
                        <button class="issue-btn" id="issue-btn" @click="issueCareID()" v-if="cg.status === 'verified' || cg.status === 'approved'">✓
                            Issue
                            Care ID &amp; Notify
                            Care Giver</button>
                    </div>

                </div>

            </div>
        </div>
    </AdminLayout>

</template>

<script lang="js">
import AdminLayout from '../../Layouts/AdminLayout.vue';
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Request from "@/utils/apiclient.js";

export default {
    components: {
        AdminLayout,
        LoadingSpinner,

    },
    data() {
        return {
            isLoading: false,
            isAdding: false,
            isUpdating: false,
            educationLevels: [],
            englishLevels: [],
            showHistory: false,
            showCareVerification: false,
            showReviews: false,
            showMalpractice: false,
            showSection: false,
            isSaving: false,
            reputation: {
                "metrics": {
                    "total_employer_reviews_stats": 0,
                    "total_monthly_reviews_stats": 0,
                    "total_employer_positive_stats": 0,
                    "total_monthly_positive_stats": 0,
                    "total_employer_negative_stats": 0,
                    "total_monthly_negative_stats": 0,
                    "employer_max_cap_reached": false,
                    "deployment_max_cap_reached": false
                },
                "pagination": {
                    "current_page": 1,
                    "has_next": false,
                    "has_previous": false,
                    "total_pages": 1,
                    "total_records": 0
                },
                "reviews": []
            },
            newEntry: {
                mom_reviewer_name: '',
                mom_phone: '',
                review_month: '',
                result: 'positive',
                mom_reviews: ''
            },
            reviewForm: {

                employment_id: "",

                review: "",

                result: "positive"
            },

            docs: {

                npf_report: {
                    file: null,
                    type: null,
                    preview: null
                }

            },
            isSavingEmployment: false,
            isSavingEmploymentReview: false,

            employmentForm: {
                name: "",
                phone: "",
                email: "",
                address: ""
            },

            errors: {},
            newCase: {
                title: '',
                description: '',
                severity: 'medium',
                status: 'open',
                escalated_by: '',
                do_not_employ: false,
                case_document: null,
            },
            cg: {

                id: null,
                care_id: null,
                status: 'pending',

                profile_photo: null,

                fn: '',
                ln: '',

                dob: null,
                gender: '',

                phone: '',
                whatsapp: '',
                email: '',

                state: '',

                exp_type: '',
                exp_years: '',

                arrangement: '',
                availability: '',

                education: '',
                english: '',

                cats: [],
                category_links: [],
                verified_categories_count: 0,
                categories_count: 0,

                // -----------------------------
                // IDENTITY
                // -----------------------------
                bvn: '',
                nin: '',

                bvn_status: "api_pending",
                nin_status: "api_pending",

                // -----------------------------
                // GUARANTOR
                // -----------------------------
                gname: '',
                gphone: '',
                grel: '',
                gocc: '',
                gaddress: '',
                gstatus: 'pending',

                // -----------------------------
                // DOCUMENTS
                // -----------------------------
                documents: {

                    id: {
                        url: null,
                        status: 'pending'
                    },

                    certificate: {
                        url: null,
                        status: 'pending'
                    },

                    guarantor: {
                        url: null,
                        status: 'pending'
                    },
                    employment: {
                        url: null,
                        status: 'pending'
                    },

                },

                // -----------------------------
                // EMPLOYMENT
                // -----------------------------
                employment: [],

                employment_verified_count: 0,

                // -----------------------------
                // BACKGROUND CHECK
                // -----------------------------
                background: {

                    provider: null,

                    status: 'pending',

                    criminal_record: false,

                    notes: ''
                },

                // -----------------------------
                // REPUTATION
                // -----------------------------
                reputation: {

                    pre: 0,

                    good: 0,

                    bad: 0,

                    warnings: 0,

                    suspensions: 0
                },

                // -----------------------------
                // MALPRACTICE
                // -----------------------------

                malpractice: {

                    count: 0,
                    cases: [
                    ]


                },

                // -----------------------------
                // VERIFICATION
                // -----------------------------


                verification: {
                    "raw_score": 0,
                    "trust_score": "",
                    "tier": "DNE",
                    "do_not_employ": false,
                    "deploy": false,
                    "scores": {
                        "bvn": 0,
                        "nin": 0,
                        "government_id": 0,
                        "criminal_clearance": 0,
                        "guarantor": 0,
                        "education": 0,
                        "certificate_score": 0,
                        "employment_score": 0,
                        "care_categories": 0,
                        "reputation_score": 0,
                        "malpractice_penalty": 0,
                        "bronze_cap_reason": "",
                        "silver_cap_reason": "",
                        "gold_gate_failed": false
                    }
                }


            },

            score_result: null,
        }
    },
    mounted() {
        this.fetchCargiverDetails();
    },

    computed: {

        initials() {
            return (this.cg?.fn?.[0] || '?') + (this.cg?.ln?.[0] || '?');
        },
        maskedBVN() {
            return this.cg?.bvn
                ? `BVN: •••••${this.cg.bvn.slice(-4)}`
                : 'Not provided';
        },

        maskedNIN() {
            return this.cg?.nin
                ? `NIN: •••••${this.cg.nin.slice(-4)}`
                : 'Not provided';
        },

        selectedEducationLevels() {
            const educationMap = new Map(
                this.educationLevels.map(edu => [edu.id, edu.name])
            );

            return educationMap.get(this.cg.education) || '';
        },

        verifiedEmploymentCount() {

            return this.cg.employment.filter(emp => emp.verified).length;
        },

        verifiedCareTypes() {
            return (this.cg.category_links || [])
                .filter(item => item.is_verified)
                .length;
        },

        // FLATTEN ALL REVIEWS
        // =====================================

        allEmployerReviews() {

            let reviews = [];

            this.cg.employment.forEach(emp => {

                if (emp.reviews && emp.reviews.length) {

                    emp.reviews.forEach(review => {

                        reviews.push({

                            ...review,

                            employment_name: emp.name
                        });
                    });
                }
            });

            return reviews;
        },


        totalEmployerReviews() {

            return this.cg.employment.reduce(
                (total, emp) => total + (emp.reviews?.length || 0),
                0
            );
        },

        totalPositiveReviews() {

            return this.cg.employment.reduce(

                (total, emp) => {

                    const count = (emp.reviews || []).filter(
                        r => r.result === "positive"
                    ).length;

                    return total + count;
                },

                0
            );
        },

        totalNegativeReviews() {

            return this.cg.employment.reduce(

                (total, emp) => {

                    const count = (emp.reviews || []).filter(
                        r => r.result === "negative"
                    ).length;

                    return total + count;
                },

                0
            );
        },
        hasOpenCases() {
            if (!this.cg.malpractice?.cases) return false;
            return this.cg.malpractice.cases.some(c => c.status !== 'resolved' && c.status !== 'dismissed');
        },

        dneReasons() {
            return this.score_result?.reason
                ? this.score_result.reason
                    .split(";")
                    .map(r => r.trim())
                    .filter(Boolean)
                : [];
        }


    },

    methods: {
        toggleSectionVisibility() {
            this.showSection = !this.showSection;
        },
        toggleMalpracticeVisibility() {
            this.showMalpractice = !this.showMalpractice;
        },
        toggleReviewsVisibility() {
            this.showReviews = !this.showReviews;
        },
        toggleHistoryVisibility() {
            this.showHistory = !this.showHistory;
        },
        toggleCareVisibility() {
            this.showCareVerification = !this.showCareVerification;
        },
        sanitizePhone(value) {

            // remove non-digits
            value = value.replace(/\D/g, '');

            // max 11 digits
            value = value.slice(0, 11);

            // enforce Nigerian format:
            // 0[1-9]0XXXXXXXX
            if (value.length >= 1 && value[0] !== '0') {
                value = '0';
            }

            // second digit must be 1-9
            if (value.length >= 2) {
                const second = value[1];

                if (!/[1-9]/.test(second)) {
                    value = value[0];
                }
            }

            // third digit must be 0
            // if (value.length >= 3 && value[2] !== '0') {
            //     value = value.slice(0, 2);
            // }

            return value;
        },

        onPhoneInput(e) {
            this.employmentForm.phone = this.sanitizePhone(e.target.value);
        },

        reviewerPhoneInput(e) {
            this.newEntry.mom_phone = this.sanitizePhone(e.target.value);
        },

        async fetchReviewsPage(pageNumber, care_id) {

            try {

                const request = new Request(

                    `/deployment/${care_id}/create-monthly-review/?page=${pageNumber}`
                );

                // Payload/params object passed as the first argument per the pattern
                await request.get(

                    (res, error) => {

                        if (error) {

                            console.error("Failed to load timeline page matrix:", error);

                            return;
                        }

                        console.log(res)

                        // ASSIGN DATA
                        this.reputation = res.reputation;

                    }
                );

            } catch (error) {

                console.error("Failed to load timeline page matrix:", error);
            }
        },

        async submitNewMonthlyReview() {

            try {

                // Validation check
                if (!this.newEntry.mom_reviewer_name || !this.newEntry.mom_reviews || !this.newEntry.review_month) {

                    alert("Please fill in all core row parameters before logging validation month.");

                    return;
                }

                const [year, month] = this.newEntry.review_month.split('-');

                const payload = {
                    ...this.newEntry,
                    review_year: year,
                    review_month: month
                };

                const request = new Request(

                    `/deployment/${this.cg.id}/create-monthly-review/`
                );

                // Note: setting isSaving=true here if you still need to track loading state,
                // but the pattern relies on the callback for completion logic.
                this.isSaving = true;

                await request.post(

                    payload,

                    async (res, error) => {

                        // Handle error inside the callback per pattern
                        if (error) {

                            console.log(error);

                            const errorMsg = error.message || "Failed to record entry logs database parameters.";

                            alert(errorMsg);

                            this.isSaving = false;

                            return;
                        }

                        // RESET FORM
                        this.newEntry = {

                            mom_reviewer_name: '',

                            mom_phone: '',

                            review_month: '',

                            result: 'positive',

                            mom_reviews: ''
                        };

                        // REFRESH LIST MATRIX
                        await this.fetchReviewsPage(1, this.cg.id);


                        this.isSaving = false;
                    }
                );

            } catch (error) {

                console.log(error);

                this.isSaving = false;
            }
        },
        async saveEmployerReview() {

            try {

                if (!this.reviewForm.employment_id) {

                    alert("Please select employer");

                    return;
                }
                this.isSavingEmploymentReview = true;
                const request = new Request(

                    `/employment/${this.reviewForm.employment_id}/reviews/create/`
                );

                await request.post(

                    this.reviewForm,

                    (res, error) => {

                        if (error) {

                            console.log(error);

                            return;
                        }

                        // FIND EMPLOYER
                        const emp = this.cg.employment.find(

                            e => e.id === this.reviewForm.employment_id
                        );

                        if (!emp.reviews) {
                            emp.reviews = [];
                        }

                        // PUSH REVIEW
                        emp.reviews.push(res.data);

                        // RESET FORM
                        this.reviewForm = {

                            employment_id: "",

                            reviewer_name: "",
                            reviewer_phone: "",

                            review: "",

                            result: "positive"
                        };
                    }
                );
                this.isSavingEmploymentReview = false;
            } catch (error) {

                console.log(error);
                this.isSavingEmploymentReview = false
            }
        },

        async saveEmployment() {
            try {

                if (!this.employmentForm.name) {

                    alert("Employer name is required");

                    return;
                }

                if (!this.employmentForm.email) {

                    alert("Employer email is required");

                    return;
                }

                if (!this.employmentForm.address) {

                    alert("Employer Address is required");

                    return;
                }

                if (!this.employmentForm.phone) {

                    alert("Employer phone is required");

                    return;
                }

                this.isSavingEmployment = true;

                const request = new Request(
                    `/caregivers/${this.cg.id}/employment/create/`
                );

                await request.post(

                    {
                        name: this.employmentForm.name,
                        phone: this.employmentForm.phone,
                        email: this.employmentForm.email,
                        address: this.employmentForm.address
                    },

                    (res, error) => {

                        if (error) {

                            console.log(error);

                            alert("Unable to save employer");

                            return;
                        }

                        console.log(res);

                        // PUSH NEW EMPLOYER
                        this.cg.employment.push(res.data);

                        // RESET FORM
                        this.employmentForm = {

                            name: "",
                            phone: "",
                            email: "",
                            address: ""
                        };
                    }
                );

            } catch (error) {

                console.log(error);

            } finally {

                this.isSavingEmployment = false;
            }
        },
        async toggleEmploymentVerification(emp) {

            try {
                this.isLoading = true;

                const previousState = !emp.verified;

                const request = new Request(
                    `/employment-history/${emp.id}/verify/`
                );

                await request.patch(

                    {
                        verified: emp.verified
                    },

                    (res, error) => {

                        if (error) {

                            emp.verified = previousState;

                            console.log(error);

                            return;
                        }

                        console.log(res);
                    }
                );

                this.isLoading = false;

            } catch (error) {

                console.log(error);
                this.isLoading = false;
            }
        },
        async toggleCareCategoryVerification(item) {

            try {
                this.isLoading = true;

                const previousState = item.is_verified;

                const request = new Request(
                    `/care-experience-category/${item.id}/`
                );

                await request.patch(

                    {
                        is_verified: item.is_verified
                    },

                    (res, error) => {

                        if (error) {

                            item.is_verified = previousState;

                            console.log(error);

                            return;
                        }

                        item.verified_at = res.data.verified_at;

                        // update counter
                        this.cg.verified_categories_count =
                            this.verifiedCareTypes;

                        console.log(res);
                    }

                );
                this.isLoading = false;
            } catch (error) {

                console.log(error);
                this.isLoading = false;
            }
        },
        formatDate(isoString) {
            return new Date(isoString).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
            });
        },
        async loadMeta() {
            try {

                this.loading = true;

                const request = new Request(
                    '/registration-meta/'
                );

                await request.get((res, error) => {

                    if (error) {
                        console.log("FETCH ERROR", error);
                        return;
                    }

                    console.log("META DATA", res);

                    this.categories = res.categories

                    this.educationLevels =
                        res.education_levels

                    this.englishLevels =
                        res.english_levels


                }, false);

            } catch (err) {

                console.log("FETCH FAILED", err);

            } finally {

                this.loading = false;
            }
        },
        backToQueue() {
            window.history.back()
        },
        formatKey(key) {
            return key
                .replace(/_/g, " ")
                .replace(/\b\w/g, l => l.toUpperCase());
        },

        triggerUpload(refName) {

            this.$refs[refName]?.click();
        },

        async handleDocUpload(event, type) {

            const file = event.target.files[0];

            if (!file) return;

            // max 5MB
            const maxSize = 5 * 1024 * 1024;

            if (file.size > maxSize) {

                this.errors[type] = 'File must not exceed 5MB';

                // reset input
                event.target.value = null;

                return;
            }

            const allowed = [
                'image/jpeg',
                'image/png',
                'image/jpg',
                'application/pdf'
            ];

            if (!allowed.includes(file.type)) {

                this.errors[type] = 'Only JPG, PNG or PDF files allowed';

                // reset input
                event.target.value = null;

                return;
            }

            // clear old error
            delete this.errors[type];

            // ensure object exists
            if (!this.docs[type]) {

                this.docs[type] = {
                    file: null,
                    type: null,
                    preview: null
                };
            }

            // clear previous preview
            this.docs[type].preview = null;

            // store file
            this.docs[type].file = file;
            this.docs[type].type = file.type;

            // IMAGE PREVIEW ONLY
            if (file.type !== 'application/pdf') {

                const reader = new FileReader();

                reader.onload = (e) => {

                    this.docs[type].preview = e.target.result;
                };

                reader.readAsDataURL(file);
            }

            // AUTO SAVE
            await this.saveVerificationReview('background_check');
        },

        removeDoc(type) {

            // clear vue state
            this.docs[type] = {
                file: null,
                preview: null,
                type: null,
            };

            // clear error
            delete this.errors[type];



            // reset native input
            const inputMap = {
                npf_report: 'npfReport',

            };

            const refName = inputMap[type];

            if (this.$refs[refName]) {
                this.$refs[refName].value = null;
            }
        },
        async fetchCargiverDetails() {
            try {

                this.isLoading = true;

                await this.loadMeta();

                const request = new Request(
                    `/verification-center/${this.$route.params.id}/`
                );

                await request.get((res, error) => {

                    if (error) {
                        console.log("FETCH ERROR", error);
                        return;
                    }

                    console.log("ADMIN DATA", res);

                    if (res) {
                        this.cg = res;

                        // IMPORTANT
                        this.cg.employment.forEach(emp => {

                            if (!emp.reviews) {
                                emp.reviews = [];
                            }

                            emp.reviewForm = {

                                reviewer_name: "",
                                reviewer_phone: "",

                                review: "",

                                result: "positive"
                            };
                        });

                        this.fetchReviewsPage(this.reputation.pagination?.current_page, res.id);


                    }




                });



            } catch (err) {

                console.log("FETCH FAILED", err);

            } finally {

                this.isLoading = false;
            }
        },

        handleCaseDocument(event) {

            const file = event.target.files[0]

            if (!file) return

            this.newCase.case_document = file
        },

        replaceCaseDocument(event, item) {

            const file = event.target.files[0]

            if (!file) return

            item.case_document = file
        },

        toggleResolved(item) {

            if (item.status === 'resolved') {

                item.status = 'investigating'

            } else {

                item.status = 'resolved'
            }
        },

        async addMalpracticeCase() {

            try {
                this.isAdding = true;

                // REQUIRED FIELDS
                const requiredFields = [
                    'title',
                    'description',
                    'escalated_by',
                ];

                // VALIDATION
                const missingFields = requiredFields.filter(field => {

                    const value = this.newCase[field];

                    return (
                        value === null ||
                        value === undefined ||
                        value === ''
                    );
                });

                // STOP IF MISSING
                if (missingFields.length > 0) {

                    this.isAdding = false;

                    missingFields.forEach(field => {
                        this.errors[field] = 'This field is required';
                    });

                    return;
                }

                const formData = new FormData()

                Object.keys(this.newCase).forEach(key => {

                    if (
                        this.newCase[key] !== null &&
                        this.newCase[key] !== undefined
                    ) {

                        formData.append(
                            key,
                            this.newCase[key]
                        )
                    }
                })


                const request = new Request(
                    `/caregivers/${this.cg.id}/malpractice/create/`
                );

                await request.postFormData(
                    formData,
                    (res, error) => {

                        if (error) {

                            console.log(error);

                            return;
                        }

                        console.log(res);

                        this.cg.malpractice.cases.unshift(
                            res
                        )

                        this.newCase = {
                            title: '',
                            description: '',
                            severity: 'medium',
                            status: 'open',
                            escalated_by: '',
                            do_not_employ: false,
                            case_document: null,
                        }
                    }
                );



                this.isAdding = false;




            } catch (error) {
                this.isAdding = false;

                console.log(error)
            }
        },

        async updateMalpracticeCase(item) {

            try {
                this.isUpdating = true;

                const formData = new FormData()

                formData.append(
                    "title",
                    item.title
                )

                formData.append(
                    "description",
                    item.description
                )

                formData.append(
                    "severity",
                    item.severity
                )

                formData.append(
                    "status",
                    item.status
                )

                formData.append(
                    "do_not_employ",
                    item.do_not_employ
                )

                formData.append(
                    "escalated_by",
                    item.escalated_by || ''
                )

                if (item.case_document instanceof File) {

                    formData.append(
                        "case_document",
                        item.case_document
                    );
                }




                const request = new Request(
                    `/malpractice/${item.id}/update/`
                );

                await request.patchFormData(
                    formData,
                    (res, error) => {

                        if (error) {

                            console.log(error);

                            return;
                        }

                        console.log(res);
                        Object.assign(
                            item,
                            res.data
                        )
                    }


                );
                this.isUpdating = false;


            } catch (error) {
                this.isUpdating = false;
                console.log(error)
            }
        },
        async saveVerificationReview(section_name) {

            try {

                this.isLoading = true;

                const formData = new FormData();

                // ---------------------------------
                // IDENTITY
                // ---------------------------------

                formData.append(
                    "bvn_status",
                    this.cg.bvn_status
                );

                formData.append(
                    "nin_status",
                    this.cg.nin_status
                );

                // ---------------------------------
                // DOCUMENTS
                // ---------------------------------
                if(this.cg.documents?.id){
                    formData.append(
                        "government_id",
                        this.cg.documents.id.status
                    );
                }

                if(this.cg.documents?.certificate){
                    formData.append(
                        "certificate_verification",
                        this.cg.documents.certificate.status
                    );
                }

                // ---------------------------------
                // BACKGROUND
                // ---------------------------------

                formData.append(
                    "background_status",
                    this.cg.background.status
                );


                if (this.docs.npf_report.file) {

                    formData.append(
                        "npf_report",
                        this.docs.npf_report.file
                    );
                }

                formData.append(
                    "education_level",
                    this.cg.education
                )

                // ---------------------------------
                // GUARANTOR
                // ---------------------------------

                formData.append(
                    "status",
                    this.cg.gstatus
                );

                // ---------------------------------
                // EMPLOYMENT
                // ---------------------------------

                formData.append(
                    "employment_verified_count",
                    this.cg.employment_verified_count
                );

                // ---------------------------------
                // REPUTATION
                // ---------------------------------

                formData.append(
                    "rep_pre",
                    this.cg.reputation.pre
                );

                formData.append(
                    "rep_good",
                    this.cg.reputation.good
                );

                formData.append(
                    "rep_bad",
                    this.cg.reputation.bad
                );

                // ---------------------------------
                // MALPRACTICE
                // ---------------------------------

                formData.append(
                    "malpractice_count",
                    this.cg.malpractice.count
                );

                console.log(formData)

                const request = new Request(
                    `/verification-center/${this.cg.id}/${section_name}/save/`
                );

                await request.patchFormData(
                    formData,
                    (res, error) => {

                        if (error) {

                            console.log(error);

                            return;
                        }

                        console.log(res);
                    }
                );

            } catch (err) {

                console.log(err);

            } finally {

                this.isLoading = false;
            }
        },
        async updateReputation(field, amount) {

            const current =
                this.cg.reputation[field] || 0;

            const updated =
                current + amount;

            // PREVENT NEGATIVE VALUES
            if (updated < 0) {
                return;
            }

            // UPDATE UI
            this.cg.reputation[field] = updated;

            // SAVE TO API
            await this.saveVerificationReview(
                'reputation'
            );
        },
        async computeScore() {

            try {


                this.isLoading = true;

                const request = new Request(
                    `/verification-center/${this.cg.id}/compute/`
                );

                await request.post(
                    {},
                    (res, error) => {

                        if (error) {

                            console.log(error);

                            return;
                        }

                        console.log("TRUST SCORE", res);
                        this.score_result = res;

                        this.cg.verification = {

                            raw_score: res.raw_score,

                            trust_score: res.final_score === 0 ? "DNE" : res.final_score,

                            tier: res.tier,

                            do_not_employ: res.do_not_employ,
                            deploy: res.deploy,

                            scores: res.breakdown,
                        };

                        console.log(this.verification)


                    }
                );

            } catch (err) {

                console.log(err);

            } finally {

                this.isLoading = false;
            }
        },
        async issueCareID() {
            try {
                if (!this.cg || !this.score_result) return;

                this.isLoading = true;

                const request = new Request(
                    `/verification-center/${this.cg.id}/issue-care-id/`
                );

                await request.post(
                    {
                        care_id: this.cg.care_id,
                    },
                    (res, error) => {
                        if (error) {
                            console.log("ISSUE CARE ID ERROR", error);
                            return;
                        }

                        console.log("CARE ID ISSUED", res);

                        // update local state if backend returns updated caregiver
                        if (res?.cg) {
                            this.cg = res.cg;
                        }

                        if (res?.care_id) {
                            this.cg.care_id = res.care_id;
                        }

                        if (res?.status) {
                            this.cg.status = res.status;
                        }

                        // optional: if backend confirms tier update
                        if (res?.tier) {
                            this.cg.verification.tier = res.tier;
                        }

                        this.$router.push({
                            name: 'careid-view',
                            params: {
                                id: this.cg.id
                            }
                        });
                    }
                );

            } catch (err) {
                console.log("ISSUE CARE ID FAILED", err);

            } finally {
                this.isLoading = false;
            }
        }
    }
}

</script>

<style>
:root {
    --green: #17865F;
    --dkgreen: #0D5C40;
    --mdgreen: #2A9D74;
    --ltgreen: #E8F4EF;
    --bg: #D6EDE2;
    --card: #EEF7F2;
    --pink: #F59CAB;
    --amber: #FF9700;
    --white: #FFFFFF;
    --text: #1A1A1A;
    --muted: #5A7A69;
    --border: #C0DDD0;
    --red: #E74C3C;
    --red-lt: #FEE2E2;
    --orange: #F97316;
    --blue: #2563EB;
    --blue-lt: #EFF6FF;
}
</style>
<style lang="css" scoped>
* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

body {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
}

/* ── SYSTEM NAV ── */
.sys-nav {
    background: var(--dkgreen);
    display: flex;
    align-items: stretch;
    border-bottom: 2px solid rgba(0, 0, 0, 0.15);
    position: sticky;
    top: 0;
    z-index: 200;
    overflow-x: auto;
}

.sys-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
}

.sys-logo-mark {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: var(--pink);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 13px;
    color: white;
    font-weight: 700;
}

.sys-logo-text {
    font-size: 9px;
    letter-spacing: 3px;
    color: rgba(255, 255, 255, 0.65);
    font-weight: 600;
}

.sys-logo-tm {
    font-size: 6px;
    color: rgba(255, 255, 255, 0.35);
    vertical-align: super;
}

.sys-steps {
    display: flex;
    flex: 1;
    min-width: 0;
}

.sys-step {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    cursor: pointer;
    border-bottom: 3px solid transparent;
    transition: 0.2s;
    position: relative;
    flex-shrink: 0;
}

.sys-step:hover {
    background: rgba(255, 255, 255, 0.05);
}

.sys-step.active {
    border-bottom-color: var(--pink);
}

.sys-step.active .step-num {
    background: var(--pink);
    color: white;
}

.sys-step.active .step-label {
    color: white;
}

.sys-step.done .step-num {
    background: rgba(39, 174, 96, 0.3);
    color: #7FD4A6;
}

.sys-step.done .step-label {
    color: rgba(255, 255, 255, 0.6);
}

.step-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.4);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: 0.2s;
}

.step-label {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.4);
    transition: 0.2s;
    white-space: nowrap;
}

.step-arrow {
    color: rgba(255, 255, 255, 0.15);
    font-size: 14px;
    flex-shrink: 0;
}

.sys-badge {
    margin: auto 16px auto auto;
    font-size: 9px;
    letter-spacing: 2px;
    color: rgba(255, 255, 255, 0.3);
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
}

/* ── VIEWS ── */
.view {
    display: block;
}

.view.active {
    display: block;
}

/* ── SHARED ── */
.page-wrap {
    max-width: 100%;
    margin: 0 auto;
    /* padding: 36px 24px 80px; */
}

.ph {
    margin-bottom: 14px;
}

.ptitle {
    font-size: 18px;
    font-weight: 600;
    color: #1A2E22;
}

.ptitle span {
    color: #17865F;
}

.page-hero {
    background: var(--dkgreen);
    padding: 36px 40px 44px;
    text-align: center;
}

.hero-tag {
    display: inline-block;
    font-size: 9px;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--pink);
    border: 1px solid rgba(245, 156, 171, 0.3);
    border-radius: 20px;
    padding: 4px 14px;
    margin-bottom: 12px;
}

.hero-title {
    font-family: 'Playfair Display', serif;
    font-size: 30px;
    color: white;
    font-weight: 700;
    margin-bottom: 8px;
    line-height: 1.25;
}

.hero-title em {
    color: var(--pink);
    font-style: italic;
}

.hero-sub {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    max-width: 480px;
    margin: 0 auto;
    line-height: 1.6;
}

/* ── PROGRESS BAR ── */
.progress-bar {
    display: flex;
    gap: 0;
    margin-bottom: 28px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid var(--border);
}

.pb-step {
    flex: 1;
    padding: 10px 14px;
    background: var(--card);
    border-right: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 8px;
    transition: 0.3s;
}

.pb-step:last-child {
    border-right: none;
}

.pb-step.active {
    background: var(--green);
}

.pb-step.done {
    background: var(--dkgreen);
}

.pb-num {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.5);
    font-size: 9px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.pb-step.active .pb-num,
.pb-step.done .pb-num {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.pb-lbl {
    font-size: 10px;
    color: var(--muted);
}

.pb-step.active .pb-lbl,
.pb-step.done .pb-lbl {
    color: white;
}

/* ── SECTION CARDS ── */
.section-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 24px 26px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.section-icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: var(--green);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
}

/* ── FORM FIELDS ── */
.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.form-grid-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.form-grid-1 {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.field label {
    font-size: 10px;
    font-weight: 500;
    color: var(--muted);
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.field label .req {
    color: var(--red);
    margin-left: 2px;
}

.field input,
.field select,
.field textarea {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 9px;
    padding: 10px 13px;
    color: var(--text);
    outline: none;
    transition: 0.2s;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
    border-color: var(--green);
    box-shadow: 0 0 0 3px rgba(23, 134, 95, 0.1);
}

.field input.invalid {
    border-color: var(--red);
    background: #FFF5F5;
}

.field textarea {
    resize: vertical;
    min-height: 80px;
}

.field select option {
    background: white;
}

.field-hint {
    font-size: 10px;
    color: var(--muted);
    margin-top: 3px;
}

/* ── PHOTO UPLOAD ── */
.photo-upload {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 14px;
}

.photo-preview {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: var(--ltgreen);
    border: 2px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    flex-shrink: 0;
    overflow: hidden;
    cursor: pointer;
    transition: 0.2s;
}

.photo-preview:hover {
    border-color: var(--green);
}

.photo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.photo-upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
}

.photo-upload-btn:hover {
    border-color: var(--green);
    background: var(--ltgreen);
}

.photo-upload-hint {
    font-size: 10px;
    color: var(--muted);
    margin-top: 5px;
}

.photo-required-note {
    font-size: 10px;
    color: var(--red);
    margin-top: 5px;
    display: none;
}

/* ── DOCUMENT UPLOAD ── */
.doc-upload-zone {
    border: 2px dashed var(--border);
    border-radius: 10px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: 0.2s;
    background: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.doc-upload-zone:hover,
.doc-upload-zone.drag {
    border-color: var(--green);
    background: var(--ltgreen);
}

.doc-icon {
    font-size: 24px;
}

.doc-upload-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
}

.doc-upload-sub {
    font-size: 10px;
    color: var(--muted);
}

.doc-upload-zone input[type=file] {
    display: none;
}

.doc-uploaded {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--ltgreen);
    border: 1px solid var(--border);
    border-radius: 8px;
    margin-top: 8px;
}

.doc-uploaded-name {
    font-size: 12px;
    color: var(--dkgreen);
    font-weight: 500;
    flex: 1;
}

.doc-remove {
    font-size: 10px;
    color: var(--red);
    cursor: pointer;
    font-weight: 600;
}

/* camera / upload dual option */
.dual-upload {
    display: flex;
    gap: 10px;
    margin-bottom: 8px;
}

.dual-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 14px 10px;
    border-radius: 10px;
    border: 1.5px dashed var(--border);
    background: white;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: var(--dkgreen);
    transition: 0.2s;
}

.dual-btn:hover {
    border-color: var(--green);
    background: var(--ltgreen);
}

.dual-btn span {
    font-size: 22px;
}

/* ── REFERENCE ROWS ── */
.ref-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 10px;
    align-items: end;
    margin-bottom: 10px;
    background: white;
    padding: 12px;
    border-radius: 9px;
    border: 1px solid var(--border);
}

.ref-row-num {
    font-size: 10px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 4px;
}

.ref-remove-btn {
    width: 30px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: white;
    color: var(--red);
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.2s;
    align-self: flex-end;
}

.ref-remove-btn:hover {
    background: var(--red-lt);
}

.field-error {
    font-size: 10px;
    color: var(--red);
    margin-top: 5px;
}

.add-emp-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px dashed var(--green);
    background: var(--ltgreen);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
    margin-top: 4px;
}

.add-ref-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px dashed var(--green);
    background: var(--ltgreen);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
    margin-top: 4px;
}

.add-ref-btn:hover {
    background: var(--card);
}



/* ─────────────────────────────
   MOM REVIEWS
───────────────────────────── */
/* =======================================================
   MONTHLY TRACKER WRAPPER
======================================================= */

.monthly-track-wrapper {
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 20px;
    overflow: hidden;
    margin-top: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

/* =======================================================
   HEADER
======================================================= */

.monthly-track-header {
    padding: 22px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    border-bottom: 1px solid #f3f3f3;
    flex-wrap: wrap;
}

.monthly-track-title-wrap {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    cursor: pointer;
}

.track-toggle-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #f5f7fb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    transition: 0.25s ease;
    color: #444;
}

.track-toggle-icon.open {
    transform: rotate(180deg);
}

.track-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
}

.track-subtitle {
    font-size: 13px;
    color: #7b8190;
    margin-top: 4px;
}

/* =======================================================
   METRICS
======================================================= */

.track-metrics-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.track-metric-card {
    min-width: 110px;
    padding: 14px 16px;
    border-radius: 14px;
    text-align: center;
}

.track-metric-card.positive {
    background: #ecfdf3;
}

.track-metric-card.negative {
    background: #fef2f2;
}

.track-metric-card.neutral {
    background: #f4f6fb;
}

.metric-value {
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
}

.track-metric-card.positive .metric-value {
    color: #0f9f6e;
}

.track-metric-card.negative .metric-value {
    color: #dc2626;
}

.track-metric-card.neutral .metric-value {
    color: #111827;
}

.metric-label {
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
}

/* =======================================================
   BODY
======================================================= */

.monthly-track-body {
    padding: 22px;
}

/* =======================================================
   FORM CARD
======================================================= */

.monthly-entry-card {
    border: 1px solid #ececec;
    border-radius: 18px;
    padding: 22px;
    background: #fafbfd;
    margin-bottom: 28px;
}

.card-header-row {
    margin-bottom: 22px;
}

.card-title {
    font-size: 17px;
    font-weight: 700;
    color: #111827;
}

.card-subtitle {
    font-size: 13px;
    color: #7b8190;
    margin-top: 4px;
}

/* =======================================================
   FORM GRID
======================================================= */

.monthly-form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 18px;
    margin-bottom: 18px;
}

.field {
    display: flex;
    flex-direction: column;
}

.field label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

/* =======================================================
   INPUTS
======================================================= */

.modern-input,
.modern-textarea {
    width: 100%;
    border: 1px solid #dfe3ea;
    border-radius: 12px;
    padding: 13px 14px;
    font-size: 14px;
    background: #fff;
    transition: 0.2s ease;
    outline: none;
}

.modern-input:focus,
.modern-textarea:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.08);
}

.modern-textarea {
    resize: vertical;
    min-height: 110px;
}

/* =======================================================
   ACTIONS
======================================================= */

.monthly-action-row {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

.submit-month-btn {
    border: none;
    background: #111827;
    color: #fff;
    padding: 13px 22px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: 0.2s ease;
}

.submit-month-btn:hover {
    transform: translateY(-1px);
    opacity: 0.94;
}

.submit-month-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* =======================================================
   LIMIT BANNER
======================================================= */

.timeline-limit-banner {
    background: #fff7ed;
    border: 1px solid #fed7aa;
    color: #9a3412;
    padding: 16px 18px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    font-size: 14px;
    font-weight: 500;
}

.limit-icon {
    font-size: 18px;
}

/* =======================================================
   TIMELINE
======================================================= */

.timeline-wrapper {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.timeline-card {
    display: flex;
    gap: 18px;
    padding: 18px;
    border-radius: 18px;
    background: #fff;
    border: 1px solid #ececec;
    transition: 0.2s ease;
}

.timeline-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.05);
}

.timeline-card.positive {
    border-left: 5px solid #10b981;
}

.timeline-card.negative {
    border-left: 5px solid #ef4444;
}

/* =======================================================
   TIMELINE LINE
======================================================= */

.timeline-line {
    position: relative;
    width: 20px;
    display: flex;
    justify-content: center;
}

.timeline-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    margin-top: 6px;
}

.timeline-dot.positive {
    background: #10b981;
}

.timeline-dot.negative {
    background: #ef4444;
}

/* =======================================================
   CONTENT
======================================================= */

.timeline-content {
    flex: 1;
}

.timeline-top-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 18px;
    flex-wrap: wrap;
}

.timeline-month {
    font-size: 13px;
    font-weight: 700;
    color: #6b7280;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.timeline-reviewer {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
}

.timeline-phone {
    margin-top: 10px;
    font-size: 13px;
    color: #6b7280;
}

.timeline-review-text {
    margin-top: 14px;
    font-size: 14px;
    line-height: 1.7;
    color: #374151;
}

/* =======================================================
   STATUS BADGE
======================================================= */

.timeline-status-badge {
    padding: 8px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    text-transform: capitalize;
}

.timeline-status-badge.positive {
    background: #ecfdf3;
    color: #047857;
}

.timeline-status-badge.negative {
    background: #fef2f2;
    color: #b91c1c;
}

/* =======================================================
   PAGINATION
======================================================= */

.timeline-pagination {
    margin-top: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
}

.pagination-btn {
    border: none;
    background: #f4f6fb;
    color: #111827;
    padding: 11px 18px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: 0.2s ease;
}

.pagination-btn:hover {
    background: #e9edf5;
}

.pagination-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.pagination-indicator {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
}

/* =======================================================
   EMPTY STATE
======================================================= */

.monthly-empty-state {
    text-align: center;
    padding: 60px 20px;
    border: 1px dashed #d6dae2;
    border-radius: 18px;
    background: #fafbfd;
}

.empty-icon {
    font-size: 42px;
    margin-bottom: 12px;
}

.empty-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
}

.empty-subtitle {
    margin-top: 8px;
    font-size: 14px;
    color: #6b7280;
}

/* =======================================================
   ANIMATION
======================================================= */

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.25s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

/* =======================================================
   RESPONSIVE
======================================================= */

@media(max-width:768px) {

    .monthly-track-header {
        flex-direction: column;
    }

    .track-metrics-group {
        width: 100%;
    }

    .track-metric-card {
        flex: 1;
    }

    .timeline-top-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .monthly-action-row {
        justify-content: stretch;
    }

    .submit-month-btn {
        width: 100%;
    }

}

/* ─────────────────────────────
   EMPLOYMENT HISTORY
───────────────────────────── */

.employment-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Header */

.employment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #fff;
}

.employment-review-form {
    width: 100%;
}

.verified-counter {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: var(--muted);
}

.verified-badge {
    min-width: 42px;
    height: 30px;
    padding: 0 12px;
    border-radius: 999px;
    background: #111827;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
}


.reviews-section-container {
    width: 100%;
    margin-top: 24px;
}

/* Header & Toggle Controls */
.reviews-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
}

.title-toggle-group {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
}

.review-list {
    display: flex;
    flex-direction: row;
    gap: 16px;
    /* Spacing between rows */
    width: 100%;
    margin: 0 auto;
}

/* Individual Row Item */
.review-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 20px;
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    /* Soft border */
    border-radius: 8px;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

/* Subtle hover effect for interactivity */
.review-item:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

/* Left Content Area */
.review-main-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    padding-right: 24px;
    /* Space before the badge */
}

/* Header Info */
.review-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.reviewer-name {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    /* Dark slate */
}

.employment-name {
    font-size: 13px;
    color: #64748b;
    /* Muted slate */
    background-color: #f1f5f9;
    padding: 2px 8px;
    border-radius: 4px;
}

/* Review Text */
.review-body {
    margin: 4px 0;
    font-size: 14px;
    line-height: 1.6;
    color: #334155;
    font-style: italic;
}

/* Footer / Contact */
.review-footer {
    font-size: 12px;
    color: #94a3b8;
}

/* Right Side / Status Badge */
.review-meta {
    display: flex;
    align-items: center;
    height: 100%;
}

.review-status {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 6px 12px;
    border-radius: 20px;
    /* Pill shape */
    white-space: nowrap;
}

/* Status Modifier Examples (Adjust class names based on your `review.result` values) */
.review-status.approved,
.review-status.positive {
    background-color: #ecfdf5;
    color: #059669;
}

.review-status.pending {
    background-color: #fffbeb;
    color: #d97706;
}

.review-status.rejected,
.review-status.negative {
    background-color: #fef2f2;
    color: #dc2626;
}

/* ─────────────────────────────
   CARD WRAPPER
───────────────────────────── */

.emp-history-wrapper {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
}


.emp-history-container {
    width: 100%;
    margin-top: 24px;
}

.emp-history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
}

.btn-toggle-history {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #f8fafc;
    border: 1px solid #cbd5e1;
    color: var(--teal);
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-toggle-history:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #94a3b8;
}

.btn-toggle-history.is-active {
    background-color: #eff6ff;
    border-color: #bfdbfe;
    color: #2563eb;
}

/* Animated Arrow Component */
.toggle-icon {
    font-size: 10px;
    transition: transform 0.2s ease;
    transform: rotate(-90deg);
}

.toggle-icon.rotated {
    transform: rotate(0deg);
}

.toggle-icon.crotated {
    transform: rotate(0deg);
}

/* Interactive Action Button Style */
.btn-toggle-action {
    align-self: flex-start;
    background-color: #f8fafc;
    border: 1px solid #cbd5e1;
    color: #475569;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-toggle-action:hover {
    background-color: #f1f5f9;
    color: #1e293b;
}

.btn-toggle-action.is-active {
    background-color: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
}

/* Main inner display container */
.care-verification-content {
    margin-top: 16px;
}

.header-main-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.title-toggle-group {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    user-select: none;
}

/* ─────────────────────────────
   CARD
───────────────────────────── */

.emp-history-row {
    background: #fff;
    border: 1px solid #dbe4dc;
    border-radius: 16px;
    padding: 14px;
    transition: .2s ease;
    min-width: 0;
    position: relative;
    overflow: hidden;
}

/* top accent */

.emp-history-row::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: var(--green);
}

/* hover */

.emp-history-row:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
}

/* ─────────────────────────────
   CARD HEADER
───────────────────────────── */

.emp-row-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;

}

.emp-title {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
}

.emp-row-num {
    font-size: 10px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
}

.emp-row-remove {
    font-size: 11px;
    color: var(--red);
    cursor: pointer;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 5px;
    border: 1px solid var(--red);
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    transition: 0.2s;
}

/* ─────────────────────────────
   VERIFY TOGGLE
───────────────────────────── */

.verify-toggle {
    display: flex;
    align-items: center;
    align-self: flex-end;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    padding: 5px 10px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: .2s ease;
}

.verify-toggle:hover {
    background: #f1f5f9;
}

.verify-toggle input {
    display: none;
}

.toggle-box {
    width: 18px;
    height: 18px;
    border-radius: 5px;
    border: 2px solid #cbd5e1;
    background: #fff;
    position: relative;
    transition: .2s ease;
    flex-shrink: 0;
}

.verify-toggle input:checked+.toggle-box {
    background: var(--green);
    border-color: var(--ltgreen);
}

.verify-toggle input:checked+.toggle-box::after {
    content: "✓";
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.verify-text {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
}

.verify-text.verified {
    color: var(--green);
}

/* ─────────────────────────────
   INFO SECTION
───────────────────────────── */

.form-grid-emp {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 10px;
}

.form-grid-emp div {
    font-size: 11px;
    line-height: 1.5;
    color: #334155;
    word-break: break-word;
}

.form-grid-emp b {
    color: #0f172a;
    font-weight: 700;
}

/* ─────────────────────────────
   RESPONSIVE
───────────────────────────── */

@media (max-width:1400px) {
    .emp-history-wrapper {
        grid-template-columns: repeat(4, 1fr);
    }
}

@media (max-width:1100px) {
    .emp-history-wrapper {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width:768px) {
    .emp-history-wrapper {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width:520px) {
    .emp-history-wrapper {
        grid-template-columns: 1fr;
    }
}

/* ── CATEGORY TOGGLES ── */
.cat-grid {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.cat-toggle {
    padding: 8px 16px;
    border-radius: 20px;
    border: 1px solid var(--border);
    background: white;
    font-size: 12px;
    font-weight: 500;
    color: var(--muted);
    cursor: pointer;
    transition: 0.2s;
    user-select: none;
}

.cat-toggle.selected {
    background: var(--green);
    border-color: var(--green);
    color: white;
}

/* ── CONSENT BOX ── */
.consent-box {
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    padding: 16px 18px;
    margin-bottom: 16px;
}

.consent-inner {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.consent-inner input[type=checkbox] {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    flex-shrink: 0;
    accent-color: var(--green);
    cursor: pointer;
}

.consent-text {
    font-size: 12px;
    color: var(--text);
    line-height: 1.6;
}

.consent-text strong {
    color: var(--dkgreen);
}


/* ── SUBMIT BTN ── */
.submit-btn {
    width: 100%;
    padding: 15px;
    border-radius: 10px;
    border: none;
    background: var(--green);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 8px;
}

.submit-btn:hover {
    background: var(--mdgreen);
}

.submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ── DOCUMENT UPLOAD ── */
.doc-uploaded-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px;
    margin-top: 14px;
    border-radius: 16px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
    transition: 0.25s ease;
}

.doc-uploaded-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.doc-thumb {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    overflow: hidden;
    flex-shrink: 0;
    background: #f3f5f7;
    display: flex;
    align-items: center;
    justify-content: center;
}

.doc-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.pdf-thumb {
    font-size: 28px;
}

.doc-remove-btn {
    border: none;
    background: #ffe9e9;
    color: #d93025;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: 0.2s ease;
}

/* ── SUCCESS STATE ── */
.success-state {
    text-align: center;
    padding: 48px 24px;
    display: none;
}

.success-state.show {
    display: block;
    animation: fadeUp 0.4s ease;
}

.success-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.success-title {
    font-family: 'Playfair Display', serif;
    font-size: 24px;
    color: var(--dkgreen);
    margin-bottom: 8px;
}

.success-sub {
    font-size: 13px;
    color: var(--muted);
    max-width: 400px;
    margin: 0 auto 24px;
    line-height: 1.6;
}

.success-ref {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    color: var(--green);
    background: var(--ltgreen);
    padding: 8px 20px;
    border-radius: 8px;
    border: 1px solid var(--border);
    display: inline-block;
}

/* ── OPS QUEUE ── */
.ops-banner {
    background: #FFFBEB;
    border: 1px solid rgba(255, 151, 0, 0.3);
    border-radius: 10px;
    padding: 12px 18px;
    margin-bottom: 20px;
    font-size: 11px;
    color: #92400E;
    display: flex;
    align-items: center;
    gap: 8px;
}

.queue-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
}

.queue-table th {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    padding: 10px 12px;
    text-align: left;
    border-bottom: 2px solid var(--border);
}

.queue-table td {
    font-size: 12px;
    color: var(--text);
    padding: 12px 12px;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
}

.queue-table tr:hover td {
    background: var(--ltgreen);
}

.status-pill {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 10px;
    border-radius: 20px;
}

.status-pill.pending {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.25);
}

.status-pill.in-progress {
    background: rgba(37, 99, 235, 0.08);
    color: #1D4ED8;
    border: 1px solid rgba(37, 99, 235, 0.2);
}

.status-pill.complete {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.review-btn {
    font-size: 11px;
    font-weight: 600;
    padding: 5px 14px;
    border-radius: 7px;
    border: 1px solid var(--border);
    background: white;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
}

.review-btn:hover {
    background: var(--ltgreen);
    border-color: var(--green);
}

/* ── OPS VERIFICATION PANEL ── */
.ops-profile {
    background: white;
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 20px 22px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
}

.ops-av {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--green), #2CC89A);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    color: white;
    font-weight: 700;
    flex-shrink: 0;
    overflow: hidden;
}

.ops-av img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ops-profile-name {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    color: var(--dkgreen);
}

.ops-profile-meta {
    font-size: 11px;
    color: var(--muted);
    margin-top: 3px;
}

.ops-profile-ref {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: var(--muted);
    margin-top: 2px;
}

/* API badge */
.api-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 9px;
    border-radius: 20px;
    margin-left: 8px;
}

.api-badge.auto {
    background: rgba(37, 99, 235, 0.08);
    color: #1D4ED8;
    border: 1px solid rgba(37, 99, 235, 0.2);
}

.api-badge.manual {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.3);
}

/* signals */
.sig-block {
    background: white;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 14px;
}

.sig-block-title {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.sig-block-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.sig-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 0;
    border-bottom: 1px solid var(--border);
}

.sig-row:last-child {
    border: none;
    padding-bottom: 0;
}

.sig-left {
    flex: 1;
}

.sig-name {
    font-size: 12px;
    font-weight: 500;
    color: var(--text);
}

.sig-sub {
    font-size: 10px;
    color: var(--muted);
    margin-top: 2px;
}

.sig-doc {
    font-size: 10px;
    color: var(--green);
    text-decoration: underline;
    cursor: pointer;
    margin-top: 2px;
}

.sig-select {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 7px;
    padding: 6px 10px;
    color: var(--text);
    outline: none;
    min-width: 200px;
    cursor: pointer;
}

.sig-select:focus {
    border-color: var(--green);
}

.sig-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
}

/* reputation counters */
.rep-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-top: 8px;
}

.rep-counter {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 12px;
    text-align: center;
}

.rep-counter-label {
    font-size: 9px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
}

.rep-ctrl {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.rev-btn {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 1px solid var(--border);
    background: white;
    color: var(--green);
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.15s;
}

.rev-btn:hover {
    background: var(--ltgreen);
    border-color: var(--green);
}

.rev-count {
    font-size: 16px;
    font-weight: 700;
    color: var(--dkgreen);
    min-width: 22px;
    text-align: center;
}

/* NPF upload */
.npf-upload {
    margin-top: 10px;
    display: block;
}

.npf-upload.show {
    display: block;
}

/* compute */
.compute-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: var(--green);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 4px;
}

.compute-btn:hover {
    background: var(--mdgreen);
}

/* ─────────────────────────────
   MALPRACTICE SECTION
───────────────────────────── */

.malpractice-section {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* HEADER */

.malpractice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    background: #fff;
}

.malpractice-subtitle {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
}

/* STATS */

.malpractice-stats {
    display: flex;
    gap: 10px;
}

.mal-stat {
    min-width: 72px;
    padding: 10px 14px;
    border-radius: 14px;
    text-align: center;
    background: #f8fafc;
}

.mal-stat span {
    display: block;
    font-size: 18px;
    font-weight: 700;
}

.mal-stat small {
    font-size: 11px;
    color: #64748b;
}

.mal-stat.danger {
    background: #fef2f2;
    color: #dc2626;
}

.mal-stat.resolved {
    background: #ecfdf5;
    color: #059669;
}

.mal-stat.pending {
    background: #fff7ed;
    color: #ea580c;
}

.malpractice-section-container {
    width: 100%;
    margin-top: 24px;
}

/* Header & Interactive Group Styling */
.malpractice-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
}


/* EMPTY */

.malpractice-empty {
    padding: 18px;
    border-radius: 14px;
    background: #ecfdf5;
    color: #059669;
    font-weight: 600;
    border: 1px solid #bbf7d0;
}

/* CARD */

.malpractice-card {
    border: 1px solid #e5e7eb;
    border-radius: 18px;
    padding: 18px;
    background: #fff;
    transition: .2s ease;
}

.malpractice-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
}

/* TOP */

.malpractice-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 14px;
}

.malpractice-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    text-transform: uppercase;
}

.malpractice-meta {
    margin-top: 4px;
    font-size: 12px;
    color: #64748b;
}

/* DESCRIPTION */

.malpractice-description {
    font-size: 13px;
    line-height: 1.6;
    color: #334155;
    margin-bottom: 16px;
}

/* SEVERITY */

.severity-badge {
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.severity-badge.low {
    background: #ecfdf5;
    color: #059669;
}

.severity-badge.medium {
    background: #eff6ff;
    color: #2563eb;
}

.severity-badge.high {
    background: #fff7ed;
    color: #ea580c;
}

.severity-badge.critical {
    background: #fef2f2;
    color: #dc2626;
}

.severity-badge.severe,
.severity-badge.max_penalty {
    background: #fef2f2;
    color: var(--red);
}

/* INFO GRID */

.malpractice-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 18px;
}

.mal-info {
    padding: 12px;
    border-radius: 12px;
    background: #f8fafc;
}

.mal-info span {
    display: block;
    font-size: 11px;
    color: #64748b;
    margin-bottom: 5px;
}

.mal-info strong {
    font-size: 13px;
    color: #111827;
}

.mal-info strong.resolved {
    color: #059669;
}

.mal-info strong.open,
.mal-info strong.investigating {
    color: #ea580c;
}

/* ACTIONS */

.malpractice-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.action-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
}

.action-toggle input {
    display: none;
}

.action-box {
    width: 18px;
    height: 18px;
    border-radius: 5px;
    border: 2px solid #cbd5e1;
    background: #fff;
    position: relative;
}

.action-toggle input:checked+.action-box {
    background: #10b981;
    border-color: #10b981;
}

.action-toggle.danger input:checked+.action-box {
    background: #dc2626;
    border-color: #dc2626;
}

.action-toggle input:checked+.action-box::after {
    content: "✓";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
}

/* RESPONSIVE */

@media (max-width:768px) {

    .malpractice-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .malpractice-grid {
        grid-template-columns: 1fr;
    }

}

/* DNE banner */
.dne-banner {
    background: #FEE2E2;
    border: 2px solid var(--red);
    border-radius: 12px;
    padding: 20px 22px;
    margin-top: 14px;
    /* display: none; */
    animation: fadeUp 0.35s ease;
}

.dne-banner.show {
    display: block;
}

.dne-title {
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    color: var(--red);
    font-weight: 700;
    margin-bottom: 6px;
}

.dne-sub {
    font-size: 13px;
    color: #7F1D1D;
    line-height: 1.6;
}

.dne-reasons {
    margin: 8px 0 0;
    padding-left: 20px;
}

.dne-reasons li {
    margin-bottom: 4px;
    line-height: 1.4;
}

/* score result */
.score-result {
    /* display: none; */
    background: var(--dkgreen);
    border-radius: 14px;
    padding: 24px 26px;
    margin-top: 14px;
}

.score-result.show {
    display: block;
    animation: fadeUp 0.35s ease;
}

.sr-top {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
}

.sr-num {
    font-family: 'Playfair Display', serif;
    font-size: 56px;
    color: white;
    line-height: 1;
    font-weight: 700;
}

.sr-denom {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.35);
    margin-top: 4px;
}

.sr-tier {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 16px;
    border-radius: 20px;
    display: inline-block;
    margin-bottom: 6px;
}

.result-notes {
    margin-top: 12px;
    font-size: 11px;
    color: rgba(255, 255, 255, .4);
    line-height: 1.6;
}

.result-notes .note.dne {
    color: #FCA5A5;
    font-weight: 600;
}

.result-notes .note {
    margin-bottom: 4px;
}

.sr-tier.dne {
    background: rgba(231, 76, 60, 0.2);
    color: #FCA5A5;
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.sr-tier.bronze {
    background: rgba(255, 151, 0, 0.2);
    color: #FFC87A;
    border: 1px solid rgba(255, 151, 0, 0.3);
}

.sr-tier.silver {
    background: rgba(200, 230, 215, 0.2);
    color: #A7D4BE;
    border: 1px solid rgba(200, 230, 215, 0.3);
}

.sr-tier.gold {
    background: rgba(255, 215, 0, 0.15);
    color: #FFD700;
    border: 1px solid rgba(255, 215, 0, 0.25);
}

.result-label {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, .4);
    margin-bottom: 14px;
}

.sr-deploy {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.45);
    text-transform: capitalize;
}

.sr-checklist {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.sr-check {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.05);
}

.sr-ck-icon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    flex-shrink: 0;
}

.sr-ck-icon.ok {
    background: rgba(39, 174, 96, 0.25);
    color: #7FD4A6;
}

.sr-ck-icon.fail {
    background: rgba(231, 76, 60, 0.2);
    color: #FCA5A5;
}

.sr-ck-lbl {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
}

.issue-btn {
    width: 100%;
    padding: 13px;
    border-radius: 10px;
    border: none;
    background: var(--pink);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 12px;
}

.issue-btn:hover {
    background: #e08a99;
}

/* ─────────────────────────────
CARE TYPE VERIFICATION
───────────────────────────── */

.care-type-card {
    background: #fff;
    border: 1px solid #e7eaf3;
    border-radius: 18px;
    padding: 20px;
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

/* HEADER */

.care-type-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.care-type-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
}

.care-type-sub {
    margin-top: 4px;
    font-size: 13px;
    color: #6b7280;
}

/* COUNTER */

.verify-counter {
    width: 70px;
    height: 70px;
    border-radius: 16px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.verify-counter-number {
    font-size: 18px;
    font-weight: 800;
    color: #15803d;
    line-height: 1;
}

.verify-counter-label {
    font-size: 12px;
    color: #166534;
    margin-top: 4px;
}

/* EMPTY */

.care-empty {
    padding: 18px;
    background: #f9fafb;
    border-radius: 12px;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
}

/* LIST */

.care-type-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* ITEM */

.care-type-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    padding: 16px;
    border: 1px solid #edf1f7;
    border-radius: 14px;
    transition: all .25s ease;
    background: #fff;
}

.care-type-item:hover {
    border-color: #dbe4f0;
    transform: translateY(-1px);
}

.care-type-item.verified {
    background: #f0fdf4;
    border-color: #bbf7d0;
}

/* LEFT */

.care-type-left {
    display: flex;
    align-items: center;
    gap: 14px;
}

/* CUSTOM CHECKBOX */

.verify-check {
    position: relative;
    width: 22px;
    height: 22px;
    cursor: pointer;
}

.verify-check input {
    opacity: 0;
    width: 0;
    height: 0;
}

.checkmark {
    position: absolute;
    inset: 0;
    border-radius: 7px;
    border: 2px solid #cbd5e1;
    background: #fff;
    transition: .2s;
}

.verify-check input:checked+.checkmark {
    background: #16a34a;
    border-color: #16a34a;
}

.verify-check input:checked+.checkmark::after {
    content: "";
    position: absolute;
    left: 6px;
    top: 2px;
    width: 5px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

/* INFO */

.care-type-info {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.care-type-name {
    font-size: 12px;
    font-weight: 600;
    color: #111827;
}

.care-type-status {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* BADGES */

.verified-badge {
    background: #dcfce7;
    color: #166534;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
}

.pending-badge {
    background: #fff7ed;
    color: #c2410c;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
}

/* VERIFIED DATE */

.verified-date {
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
}

/* SUMMARY */

.care-summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
}

.summary-item {
    background-color: #f8fafc;
    padding: 10px 14px;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.summary-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
}

.summary-value {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
}

.summary-value.green-text {
    color: #16a34a;
}

.summary-value.orange-text {
    color: #ea580c;
}

/* Empty States */
.care-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #fffbeb;
    border: 1px dashed #fcd34d;
    color: #b45309;
    padding: 14px;
    border-radius: 6px;
    font-size: 13px;
}

/* MOBILE */

@media(max-width:768px) {

    .care-type-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .care-type-item {
        flex-direction: column;
        align-items: flex-start;
    }

    .verified-date {
        margin-left: 36px;
    }

    .care-summary {
        grid-template-columns: 1fr;
    }
}

/* ── EMPLOYER VIEW (standard) ── */
.ev-card {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    max-width: 480px;
    margin: 0 auto;
}

.ev-stripe {
    height: 5px;
    background: linear-gradient(90deg, var(--green), #2CC89A, var(--pink));
}

.ev-body {
    padding: 24px 26px;
}

.ev-hdr {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid #EEF4F1;
}

.ev-photo {
    width: 52px;
    height: 52px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--green), #2CC89A);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    color: white;
    font-weight: 700;
    flex-shrink: 0;
    overflow: hidden;
}

.ev-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ev-name {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    color: var(--text);
    font-weight: 700;
}

.ev-sc {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: var(--muted);
    margin-top: 3px;
}

.ev-tbadge {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 14px;
    border-radius: 20px;
}

.ev-tbadge.gold {
    background: rgba(255, 215, 0, 0.1);
    color: #B8860B;
    border: 1px solid rgba(255, 215, 0, 0.25);
}

.ev-tbadge.silver {
    background: var(--ltgreen);
    color: var(--dkgreen);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.ev-tbadge.bronze {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.2);
}

.ev-score-block {
    display: flex;
    align-items: center;
    gap: 14px;
    background: var(--ltgreen);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 18px;
    border: 1px solid rgba(23, 134, 95, 0.12);
}

.ev-score-num {
    font-family: 'Playfair Display', serif;
    font-size: 46px;
    color: var(--dkgreen);
    line-height: 1;
    font-weight: 700;
}

.ev-score-denom {
    font-size: 12px;
    color: var(--muted);
}

.ev-score-level {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 12px;
    border-radius: 20px;
    margin-top: 4px;
}

.ev-score-level.gold {
    background: rgba(255, 215, 0, 0.1);
    color: #B8860B;
    border: 1px solid rgba(255, 215, 0, 0.2);
}

.ev-score-level.silver {
    background: rgba(23, 134, 95, 0.1);
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.ev-score-level.bronze {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.15);
}

.ev-cats-lbl {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 7px;
}

.ev-cats {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.ev-cat {
    font-size: 10px;
    color: var(--dkgreen);
    font-weight: 500;
    background: var(--ltgreen);
    border: 1px solid var(--border);
    padding: 4px 10px;
    border-radius: 20px;
}

.ev-br-lbl {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 10px;
}

.ev-row-item {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.ev-rl {
    font-size: 11px;
    color: var(--text);
    width: 100px;
    flex-shrink: 0;
}

.ev-bar-track {
    flex: 1;
    height: 6px;
    background: #EEF4F1;
    border-radius: 6px;
    overflow: hidden;
}

.ev-bar-fill {
    height: 100%;
    border-radius: 6px;
}

.ev-bar-fill.excellent {
    background: var(--green);
    width: 92%;
}

.ev-bar-fill.good {
    background: var(--amber);
    width: 70%;
}

.ev-bar-fill.average {
    background: #F0A05A;
    width: 48%;
}

.ev-bar-fill.poor {
    background: var(--red);
    width: 24%;
}

.ev-rval {
    font-size: 10px;
    font-weight: 600;
    width: 58px;
    text-align: right;
}

.ev-rval.excellent {
    color: var(--green);
}

.ev-rval.good {
    color: #B36800;
}

.ev-rval.average {
    color: #E07020;
}

.ev-rval.poor {
    color: var(--red);
}

.ev-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 14px;
    background: #E8F7F0;
    border-radius: 10px;
    border: 1px solid rgba(23, 134, 95, 0.2);
    margin: 14px 0;
}

.ev-sdot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--green);
    flex-shrink: 0;
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1
    }

    50% {
        opacity: 0.5
    }
}

.ev-stext {
    font-size: 12px;
    color: var(--dkgreen);
    font-weight: 600;
}

.ev-sdate {
    font-size: 10px;
    color: var(--muted);
    margin-left: auto;
}

.ev-qr-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 14px;
    border-top: 1px solid #EEF4F1;
}

.ev-qr-box {
    width: 60px;
    height: 60px;
    background: var(--ltgreen);
    border-radius: 8px;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ev-share-btns {
    display: flex;
    gap: 8px;
}

.ev-share-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    transition: 0.2s;
}

.ev-share-btn.wa {
    background: #25D366;
    color: white;
}

.ev-share-btn.wa:hover {
    background: #1DA851;
}

.ev-share-btn.copy {
    background: var(--ltgreen);
    color: var(--dkgreen);
    border: 1px solid var(--border);
}

.ev-share-btn.copy:hover {
    background: #D0EBE0;
}

.ev-footer-lnk {
    font-size: 9px;
    color: var(--muted);
    text-align: center;
    margin-top: 12px;
}

.ev-footer-lnk a {
    color: var(--green);
    font-weight: 600;
    text-decoration: none;
}

/* ── DETAILED EMPLOYER VIEW (payment gated) ── */
.pay-gate {
    background: var(--dkgreen);
    border-radius: 16px;
    overflow: hidden;
    max-width: 560px;
    margin: 0 auto 20px;
}

.pay-gate-body {
    padding: 32px;
}

.pay-gate-icon {
    font-size: 36px;
    margin-bottom: 12px;
}

.pay-gate-title {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    color: white;
    margin-bottom: 8px;
}

.pay-gate-sub {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.55);
    line-height: 1.6;
    margin-bottom: 24px;
}

.pay-features {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
}

.pay-feature {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
}

.pay-feature-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--pink);
    flex-shrink: 0;
}

.pay-price {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 20px;
}

.pay-amount {
    font-family: 'Playfair Display', serif;
    font-size: 32px;
    color: white;
    font-weight: 700;
}

.pay-period {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.45);
}

.pay-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: var(--pink);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
}

.pay-btn:hover {
    background: #e08a99;
}

.pay-note {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.3);
    text-align: center;
    margin-top: 10px;
}

/* detailed report */
.det-report {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    max-width: 600px;
    margin: 0 auto;
    display: none;
    animation: fadeUp 0.4s ease;
}

.det-report.show {
    display: block;
}

.det-header {
    background: var(--dkgreen);
    padding: 24px 26px;
    display: flex;
    align-items: center;
    gap: 14px;
}

.det-photo {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    color: white;
    flex-shrink: 0;
    overflow: hidden;
}

.det-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.det-title {
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    color: white;
    font-weight: 700;
}

.det-sub {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 3px;
}

.det-badge {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 14px;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;
}

.det-body {
    padding: 24px 26px;
}

.det-section {
    margin-bottom: 22px;
}

.det-section-title {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.det-section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.det-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 0;
    border-bottom: 1px solid var(--border);
    font-size: 12px;
}

.det-row:last-child {
    border: none;
}

.det-key {
    color: var(--muted);
}

.det-val {
    font-weight: 500;
    color: var(--text);
}

.det-status {
    font-size: 10px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 20px;
}

.det-status.verified {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.not-verified {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.3);
}

.det-status.forged {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-status.clear {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.record {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-status.positive {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.negative {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-warning {
    background: var(--red-lt);
    border: 1px solid rgba(231, 76, 60, 0.3);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 11px;
    color: #7F1D1D;
    margin-top: 12px;
}

/* ── CARE ID ── */
.cid-cat {
    font-size: 8px;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 20px;
}

.cid-cat-dark {
    color: rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
}

.cid-cat-light {
    color: var(--dkgreen);
    background: var(--ltgreen);
    border: 1px solid var(--border);
}

.cid-share-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 9px 10px;
    border-radius: 9px;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    transition: 0.18s;
}

.cid-share-btn.wa {
    background: #25D366;
    color: white;
}

.cid-share-btn.wa:hover {
    background: #1DA851;
}

.cid-share-btn.copy {
    background: white;
    color: var(--dkgreen);
    border: 1px solid var(--border);
}

.cid-share-btn.copy:hover {
    background: var(--ltgreen);
}

/* ── UTILITIES ── */
@keyframes fadeUp {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.notice {
    padding: 14px 18px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.6;
    margin-bottom: 16px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.notice.info {
    background: rgba(37, 99, 235, 0.06);
    border: 1px solid rgba(37, 99, 235, 0.15);
    color: #1D4ED8;
}

.notice.warn {
    background: #FFFBEB;
    border: 1px solid rgba(255, 151, 0, 0.3);
    color: #92400E;
}

.notice.danger {
    background: var(--red-lt);
    border: 1px solid rgba(231, 76, 60, 0.3);
    color: #7F1D1D;
}
</style>